import React, { useState, useEffect, useTransition, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { useResumeEpoch, useTapEpoch } from '../contexts/ResumeContext';
import { formatEventLocation } from '../utils/locationFormat';
import { getDaysUntilEvent } from '../utils/dateFormatter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { TOAST } from '../utils/toastMessages';
import { Sparkles, LogOut, Calendar, MapPin, Users, DollarSign, Search, Star, User, Upload, MessageSquare, CreditCard, CheckCircle, Inbox, ClipboardList, Settings, ExternalLink, Home, Info, Check, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, X, HelpCircle, Copy, PartyPopper, Plus, MoreVertical, Archive, Trash2, Store, Clock, Mail } from 'lucide-react';
import { SectionLabel } from '../components/ui/SectionLabel';
import { CeleyLoader } from '../components/ui/CeleyLoader';
import FeedbackDialog from '../components/FeedbackDialog';
import ServiceAgreementDialog from '../components/ServiceAgreementDialog';
import PaymentCheckout from '../components/PaymentCheckout';
import ConversationSwitcher from '../components/conversation/ConversationSwitcher';
import MessagingThread from '../components/MessagingThread';
import Navigation from '../components/Navigation';
import InquiryCard from '../components/customer/InquiryCard';
import MessageConversationCard from '../components/customer/MessageConversationCard';
import ProfileForm from '../components/customer/ProfileForm';
import AccountSettings from '../components/customer/AccountSettings';
import CustomerDialogs from '../components/customer/CustomerDialogs';
import ImageCropDialog from '../components/ImageCropDialog';
import RoleSwitcher from '../components/RoleSwitcher';
import BottomTabBar from '../components/BottomTabBar';

import HelpSupportSection from '../components/HelpSupportSection';
import AccountPage from '../components/AccountPage';
import ReferralRewardsCard from '../components/ReferralRewardsCard';
import { InquiryListSkeleton, MessageListSkeleton } from '../components/skeletons';
import { formatShortDate, formatLongDate } from '../utils/dateFormatter';
import CeleyDelightLayer from '../components/CeleyDelightLayer';
import { getEventTheme, tintBadgeStyle, DECO_GRADIENTS, getEventCopy, getMood } from '../services/eventThemeService';
import { EventDecorations } from '../components/EventDecorations';
import { isConciergeManualMode } from '../components/operations/manualModeFlag';

// EventsList component with celebration logic and archive/delete
const EventsList = ({ events, inquiries, navigate, API, tileNavState, bookings, briefReadyEventIds }) => {
  // Phase A — Manual Mode gate (ADR-CONCIERGE-PHASE-A). Read once per
  // render; defaults to TRUE so misconfiguration cannot accidentally
  // expose Hybrid-Mode surfaces on the dashboard.
  const manualMode = isConciergeManualMode();
  const [celebratingEventId, setCelebratingEventId] = useState(null);
  const [celebratingEventType, setCelebratingEventType] = useState('');
  const [menuOpen, setMenuOpen] = useState(null); // event id with open menu
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedEvents, setArchivedEvents] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);

  // Single source of truth for events view mode. Seeded from tileNavState on mount.
  const [eventsViewMode, setEventsViewMode] = useState(tileNavState === 'bookings' ? 'bookings' : 'all');

  // Re-seed if tileNavState changes externally (e.g. user taps tile while already on Events)
  useEffect(() => {
    if (tileNavState === 'bookings') setEventsViewMode('bookings');
    else setEventsViewMode('all');
  }, [tileNavState]);

  // Compute events-with-bookings for Bookings tile destination
  const cancelledBkStatuses = ['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'];
  const bookedEventIds = new Set(
    (bookings || []).filter(b => !cancelledBkStatuses.includes(b.status)).map(b => b.event_id)
  );
  const allEventsWithBookings = events.filter(e => bookedEventIds.has(e.id));
  
  // Check for newly fully booked events that haven't been celebrated
  useEffect(() => {
    const checkForCelebrations = async () => {
      const now = new Date();
      
      for (const event of events) {
        // Skip if already celebrated
        if (event.fully_booked_celebrated) continue;
        
        // Check if fully booked
        const eventInquiries = inquiries.filter(i => i.event_id === event.id);
        if (eventInquiries.length === 0) continue;
        
        const allBooked = eventInquiries.every(i => 
          ['deposit_paid', 'in_progress', 'completed'].includes(i.status)
        );
        
        if (allBooked && new Date(event.event_date) >= now) {
          // Trigger celebration!
          setCelebratingEventId(event.id);
          setCelebratingEventType(event.event_type);
          
          // Mark as celebrated in backend
          try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/events/${event.id}/celebrate`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (error) {
            console.error('Failed to mark event as celebrated:', error);
          }
          
          // Only celebrate one event at a time
          break;
        }
      }
    };
    
    if (events.length > 0 && inquiries.length > 0) {
      checkForCelebrations();
    }
  }, [events, inquiries, API]);

  // Archive/delete handlers
  const handleArchive = async (e, eventId) => {
    e.stopPropagation();
    setMenuOpen(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/events/${eventId}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event archived');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive');
    }
  };

  const handleRestore = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/events/${eventId}/restore`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event restored');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/events/${deleteConfirmId}/safe-delete`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event permanently deleted');
      setDeleteConfirmId(null);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot delete this event');
      setDeleteConfirmId(null);
    }
  };

  const fetchArchived = async () => {
    if (archivedEvents.length > 0) { setShowArchived(!showArchived); return; }
    setLoadingArchived(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/events/me?include_archived=true`, { headers: { Authorization: `Bearer ${token}` } });
      setArchivedEvents((res.data || []).filter(e => e.archived));
      setShowArchived(true);
    } catch (err) {
      toast.error('Failed to load archived events');
    } finally {
      setLoadingArchived(false);
    }
  };

  // Check if event can be safely deleted (no bookings/payments)
  const canDelete = (event) => {
    const eventInquiries = inquiries.filter(i => i.event_id === event.id);
    const hasBookings = eventInquiries.some(i => ['deposit_paid', 'in_progress', 'completed', 'final_paid', 'fully_paid'].includes(i.status));
    return !hasBookings;
  };

  // Inactive detection (30+ days no activity)
  const isInactive = (event) => {
    const updatedAt = new Date(event.updated_at || event.created_at);
    const daysSinceUpdate = Math.floor((new Date() - updatedAt) / (1000 * 60 * 60 * 24));
    return daysSinceUpdate >= 30;
  };
  
  const now = new Date();
  
  // Categorize events by backend status (single source of truth).
  // B-EXP-6.23 — Concierge workspace exposes internal statuses to
  // /events/me (claimed, reviewing, researching, waiting_customer,
  // waiting_vendor, recommendations_sent, followup_needed) that the
  // customer app was never taught. For customer bucketing purposes
  // any of these are "the case is being worked on" and belong in
  // the same active/planning group as self-serve 'draft'/'planning'.
  // Without this, a reopened case (status='claimed') would vanish
  // from every My Events grouping, regressing the 6.22 reopen path.
  const CONCIERGE_ACTIVE_STATUSES = new Set([
    'new', 'claimed', 'reviewing', 'researching',
    'waiting_customer', 'waiting_vendor',
    'recommendations_sent', 'followup_needed',
  ]);
  const _isConciergeActive = (e) => e.planning_style === 'concierge'
    && CONCIERGE_ACTIVE_STATUSES.has(e.status);

  const planningEvents = events.filter(e => 
    ['draft', 'planning'].includes(e.status) || _isConciergeActive(e)
  );
  
  const bookedEvents = events.filter(e => 
    ['booked', 'final'].includes(e.status)
  );
  
  const completedEvents = events.filter(e => 
    // B-EXP-6.22 — a closed Concierge case has graduated into the
    // normal Celey customer experience. Semantically the event has
    // wrapped up (no active Concierge/marketplace work), so it sits
    // in the same bucket as `completed` self-serve events. Without
    // this the graduated event would appear in `/events/me` yet be
    // invisible on the customer's event dashboard.
    ['completed', 'closed'].includes(e.status)
  );

  const cancelledEvents = events.filter(e => 
    ['cancelled'].includes(e.status)
  );

  // Active planning = non-completed, non-cancelled
  const activePlanningEvents = planningEvents;

  // Events with bookings (for Bookings tile destination)
  const cancelledBookingStatuses = ['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'];
  const eventIdsWithBookings = new Set(
    bookings
      .filter(b => !cancelledBookingStatuses.includes(b.status))
      .map(b => b.event_id)
  );
  const eventsWithBookings = events.filter(e => eventIdsWithBookings.has(e.id));

  // Status display helpers — now driven by backend status.
  // Visual polish (Feb 2026): each pill ships with a matching 1px border so
  // the status reads as an intentional chip instead of a flat color block.
  // Color tokens otherwise unchanged.
  //
  // B-EXP-6.23 — closed Concierge cases have graduated. They read as
  // completed on the dashboard rather than falling through to the
  // "Planning" fallback (which reads as ACTIVE and is wrong).
  const getStatusDisplay = (event) => {
    const bookedCount = event.vendors_booked || 0;
    const totalVendors = (inquiries.filter(i => i.event_id === event.id)).length;

    // B-EXP-6.23 — Concierge graduation state, checked FIRST so it
    // overrides the generic status pills below. Two variants:
    //   * closed + published brief → "Brief ready" (still actionable)
    //   * closed w/o brief         → "Concierge complete" (past)
    if (event.status === 'closed' && event.planning_style === 'concierge') {
      const briefReady = briefReadyEventIds && briefReadyEventIds.has(event.id);
      if (briefReady) {
        return { label: 'Brief ready', color: 'bg-violet-100 text-violet-700 border border-violet-200' };
      }
      return { label: 'Concierge complete', color: 'bg-violet-50 text-violet-600 border border-violet-100' };
    }

    if (totalVendors > 0 && bookedCount > 0 && bookedCount >= totalVendors) {
      return { label: 'Fully Booked', color: 'bg-emerald-100 text-emerald-700 border border-emerald-200' };
    }
    
    if (event.status === 'draft') {
      return { label: 'Draft', color: 'bg-gray-100 text-gray-600 border border-gray-200' };
    }
    if (event.status === 'planning') {
      if (bookedCount > 0) {
        return { label: `${bookedCount} Booked`, color: 'bg-blue-100 text-blue-700 border border-blue-200' };
      }
      return { label: 'Vendors Contacted', color: 'bg-blue-100 text-blue-700 border border-blue-200' };
    }
    if (event.status === 'booked') {
      return { label: `${bookedCount || ''} Booked`.trim(), color: 'bg-blue-100 text-blue-700 border border-blue-200' };
    }
    if (event.status === 'final') {
      return { label: 'Final Prep', color: 'bg-amber-100 text-amber-700 border border-amber-200' };
    }
    if (event.status === 'completed') {
      return { label: 'Completed', color: 'bg-gray-100 text-gray-600 border border-gray-200' };
    }
    if (event.status === 'cancelled') {
      return { label: 'Cancelled', color: 'bg-red-100 text-red-600 border border-red-200' };
    }
    return { label: 'Planning', color: 'bg-gray-100 text-gray-600 border border-gray-200' };
  };

  // B-EXP-6.23 — safe date formatter. `new Date(null|undefined|'').toLocaleDateString()`
  // returns the string "Invalid Date", which was visibly leaking onto
  // Home hero cards, My Events cards, and Event Detail headers for
  // Concierge events that never had `event_date` set. This helper
  // returns a graceful fallback ("Date not set") for all invalid inputs
  // and preserves valid rendering for real dates.
  const formatEventDate = (dateStr, opts = { month: 'short', day: 'numeric', year: 'numeric' }, fallback = 'Date not set') => {
    if (!dateStr) return fallback;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return fallback;
    return d.toLocaleDateString('en-US', opts);
  };

  const getVendorCountText = (event) => {
    const count = event.selected_vendors?.length || 0;
    if (count === 0) return 'No vendors yet';
    return `${count} vendor${count !== 1 ? 's' : ''}`;
  };

  const renderEventCard = (event, isArchived = false) => {
    const status = getStatusDisplay(event);
    const daysUntil = getDaysUntilEvent(event.event_date);
    const isCelebrating = celebratingEventId === event.id;
    const isBooked = status.label === 'Fully Booked';
    const inactive = !isArchived && isInactive(event) && !isBooked;
    const eventTheme = getEventTheme(event.event_type);
    // B-EXP-6.23 — closed Concierge case has graduated; do NOT show
    // active-planning copy ("Your Concierge is working on this",
    // "Check your email") once the case is closed.
    const isClosedConcierge = event.status === 'closed' && event.planning_style === 'concierge';
    const hasValidDate = !!event.event_date && !Number.isNaN(new Date(event.event_date).getTime());
    // B-EXP-6.23 — prefer canonical title (merged from event_overrides
    // by /events/me), fall back to event_type, then "Event".
    const displayTitle = (event.title && String(event.title).trim())
      || event.event_type
      || 'Event';
    
    return (
      <div 
        key={event.id}
        className={`relative p-5 rounded-2xl bg-white border border-gray-100/80 shadow-tile hover:shadow-card hover:bg-gray-50/30 cursor-pointer transition-all duration-150 active:scale-[0.99] ${
          isCelebrating 
            ? 'border-emerald-300 bg-gradient-to-r from-emerald-50 to-white shadow-md shadow-emerald-100/40' 
            : ''
        } ${inactive ? 'opacity-60' : ''}`}
        style={!isCelebrating ? { borderLeftWidth: '4px', borderLeftColor: eventTheme.accentColor } : undefined}
        data-testid={`event-card-${event.id}`}
      >
        {/* Main clickable area */}
        <div onClick={() => !isArchived && navigate(`/events/${event.id}`)} className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <h3 className="font-semibold text-gray-900 text-sm">{displayTitle}</h3>
              <Badge className={`text-[11px] font-medium px-1.5 py-0 ${status.color} ${isCelebrating ? 'animate-pulse' : ''}`}>
                {isBooked && <CheckCircle className="w-3 h-3 mr-0.5 inline" />}
                {status.label}
              </Badge>
              {inactive && (
                <Badge className="text-[10px] font-normal bg-gray-50 text-gray-400 px-1.5 py-0">Inactive</Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatEventDate(event.event_date)}
                {hasValidDate && daysUntil >= 0 && daysUntil <= 30 && !isClosedConcierge && (
                  <span className="text-emerald-600 font-medium ml-0.5">
                    ({daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil}d`})
                  </span>
                )}
              </span>
              {(event.location_city || event.location_zip) && (
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{formatEventLocation(event) || event.location_zip}</span>
              )}
              <span className="flex items-center gap-1 text-gray-400"><Users className="w-3 h-3" />{getVendorCountText(event)}</span>
            </div>
            {/* Phase 8.6 — calm pre-graduation Concierge label */}
            {event.planning_style === 'concierge'
              && !['cancelled', 'completed', 'closed'].includes(event.status)
              && (inquiries.filter(i => i.event_id === event.id).length === 0) && (
              <p
                className="mt-1.5 text-[11px] text-violet-600/80 font-medium"
                data-testid={`event-card-concierge-label-${event.id}`}
              >
                Your Concierge is working on this
              </p>
            )}
            {/* Phase A — Manual Mode dashboard pill (ADR-CONCIERGE-PHASE-A).
                Calm "Check your email" expectation setter on every
                active Concierge event. In Hybrid Mode (Phase D) this
                collapses to the Phase 9A "Brief ready" pill below.
                Pill styling is shared so the visual weight matches.
                B-EXP-6.23 — hidden on closed Concierge cases (they've
                graduated — the case is done, the email is stale). */}
            {event.planning_style === 'concierge'
              && !['cancelled', 'completed', 'closed'].includes(event.status)
              && manualMode && (
              <span
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium"
                data-testid={`event-card-manual-mode-pill-${event.id}`}
              >
                <Mail className="w-3 h-3" aria-hidden />
                Check your email
              </span>
            )}
            {/* Phase 9A — Brief ready pill. Hybrid Mode only.
                Surfaced when the operations projection reports a
                sealed brief. Click is handled by the parent event
                card link, so the pill is non-interactive on its own. */}
            {event.planning_style === 'concierge'
              && !['cancelled', 'completed'].includes(event.status)
              && !manualMode
              && briefReadyEventIds && briefReadyEventIds.has(event.id) && (
              <span
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium"
                data-testid={`event-card-brief-ready-pill-${event.id}`}
              >
                Brief ready
              </span>
            )}
          </div>

          {/* 3-dot menu — hidden for cancelled/completed events */}
          {!isArchived && !['cancelled', 'completed'].includes(event.status) && (
            <div className="relative shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === event.id ? null : event.id); }}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid={`event-menu-${event.id}`}
              >
                <MoreVertical className="w-4 h-4 text-gray-400" />
              </button>
              {menuOpen === event.id && (
                <div className="absolute right-0 top-9 w-40 bg-white rounded-xl shadow-lg ring-1 ring-gray-100 z-50 py-1" data-testid={`event-menu-dropdown-${event.id}`}>
                  <button
                    onClick={(e) => handleArchive(e, event.id)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    data-testid={`archive-event-${event.id}`}
                  >
                    <Archive className="w-3.5 h-3.5" /> Archive
                  </button>
                  {canDelete(event) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpen(null); setDeleteConfirmId(event.id); }}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      data-testid={`delete-event-${event.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Restore button for archived */}
          {isArchived && (
            <Button variant="outline" size="sm" className="rounded-lg text-xs shrink-0" onClick={() => handleRestore(event.id)} data-testid={`restore-event-${event.id}`}>
              Restore
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Keyframes for hero card animations */}
      <style>{`
        @keyframes heroLift {
          from { opacity: 0; transform: scale(0.98) translateY(6px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes confettiFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      {/* Fully Booked Celebration Overlay */}
      {celebratingEventId && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm animate-in fade-in-0 duration-300"
          data-testid="celebration-overlay"
        >
          <div className="relative bg-white rounded-3xl shadow-2xl max-w-sm mx-6 p-8 text-center animate-in zoom-in-95 duration-500 overflow-hidden">
            {/* Celebration particles — unified delight system */}
            <CeleyDelightLayer mode="celebration" mood={getMood(celebratingEventType)} />

            {/* Checkmark icon */}
            <div className="relative w-20 h-20 mx-auto mb-5 bg-emerald-50 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
              <div className="absolute inset-0 rounded-full border-2 border-emerald-200 animate-ping opacity-30" />
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {celebratingEventType
                ? `Your ${celebratingEventType.toLowerCase()} is fully booked!`
                : 'Your event is fully booked!'}
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              {getEventCopy(celebratingEventType).sub || 'Everything is coming together beautifully.'}
            </p>

            <div className="space-y-2.5">
              <Button
                onClick={() => {
                  setCelebratingEventId(null);
                  navigate(`/events/${celebratingEventId}`);
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-full py-2.5"
                data-testid="celebration-view-event-btn"
              >
                View Event
              </Button>
              <Button
                variant="ghost"
                onClick={() => setCelebratingEventId(null)}
                className="w-full text-gray-400 hover:text-gray-600"
                data-testid="celebration-dismiss-btn"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* View mode segmented control */}
      {allEventsWithBookings.length > 0 && (
        <div className="flex items-center gap-1 p-0.5 bg-gray-100 rounded-lg w-fit" data-testid="events-view-mode">
          <button
            onClick={() => setEventsViewMode('all')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${eventsViewMode === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="events-view-all"
          >
            All Events
          </button>
          <button
            onClick={() => setEventsViewMode('bookings')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${eventsViewMode === 'bookings' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="events-view-bookings"
          >
            With Bookings
            <span className="ml-1 text-[10px] text-gray-400">{allEventsWithBookings.length}</span>
          </button>
        </div>
      )}

      {/* Booked Section — show first */}
      {bookedEvents.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-emerald-600 uppercase tracking-wider px-1 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Booked
          </h3>
          <div className="space-y-2">
            {bookedEvents.map(event => renderEventCard(event))}
          </div>
        </div>
      )}

      {/* Planning Section — hidden when viewing bookings (unless they have bookings) */}
      {eventsViewMode !== 'bookings' ? (
        activePlanningEvents.length > 0 && (
          <div className="space-y-3">
            <SectionLabel className="px-1">Planning</SectionLabel>
            <div className="space-y-2">
              {activePlanningEvents.map(event => renderEventCard(event))}
            </div>
          </div>
        )
      ) : (
        (() => {
          const planningWithBookings = activePlanningEvents.filter(e => bookedEventIds.has(e.id));
          return planningWithBookings.length > 0 && (
            <div className="space-y-3">
              <SectionLabel className="px-1">Planning (with bookings)</SectionLabel>
              <div className="space-y-2">
                {planningWithBookings.map(event => renderEventCard(event))}
              </div>
            </div>
          );
        })()
      )}

      {/* Completed Section — hidden when viewing bookings unless they have bookings */}
      {eventsViewMode !== 'bookings' ? (
        completedEvents.length > 0 && (
          <div className="space-y-3">
            <SectionLabel className="px-1">Past</SectionLabel>
            <div className="space-y-2 opacity-75">
              {completedEvents.map(event => renderEventCard(event))}
            </div>
          </div>
        )
      ) : (
        (() => {
          const completedWithBookings = completedEvents.filter(e => bookedEventIds.has(e.id));
          return completedWithBookings.length > 0 && (
            <div className="space-y-3">
              <SectionLabel className="px-1">Completed</SectionLabel>
              <div className="space-y-2 opacity-75">
                {completedWithBookings.map(event => renderEventCard(event))}
              </div>
            </div>
          );
        })()
      )}

      {/* Cancelled Section — collapsed by default, hidden in bookings view */}
      {cancelledEvents.length > 0 && eventsViewMode !== 'bookings' && (
        <div className="pt-2 border-t border-gray-100">
          <button
            onClick={() => setShowCancelled(!showCancelled)}
            className="w-full flex items-center justify-between py-2 px-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="toggle-cancelled-events"
          >
            <span className="flex items-center gap-1.5">
              <X className="w-3.5 h-3.5" />
              {showCancelled ? 'Hide' : 'Show'} Cancelled ({cancelledEvents.length})
            </span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showCancelled ? 'rotate-180' : ''}`} />
          </button>
          {showCancelled && (
            <div className="space-y-2 mt-2 opacity-60">
              {cancelledEvents.map(event => renderEventCard(event))}
            </div>
          )}
        </div>
      )}

      {/* Archived Section — collapsible */}
      <div className="pt-2 border-t border-gray-100">
        <button
          onClick={fetchArchived}
          className="w-full flex items-center justify-between py-2 px-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
          data-testid="toggle-archived-events"
        >
          <span className="flex items-center gap-1.5">
            <Archive className="w-3.5 h-3.5" />
            {showArchived ? 'Hide' : 'Show'} Archived Events
            {archivedEvents.length > 0 && ` (${archivedEvents.length})`}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showArchived ? 'rotate-180' : ''}`} />
        </button>
        {showArchived && (
          <div className="space-y-2 mt-2">
            {loadingArchived ? (
              <div className="py-4"><CeleyLoader inline size="sm" label="Loading archived" testid="archived-events-loader" /></div>
            ) : archivedEvents.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No archived events</p>
            ) : (
              archivedEvents.map(event => renderEventCard(event, true))
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm mx-6 p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete this event?</h3>
            <p className="text-sm text-gray-500 mb-6">This will permanently delete the event and all associated requests. This action cannot be undone.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, login, setActiveRole, activeRole } = useAuth();
  // Resume Engine subscription — bumps when iOS/Capacitor reports the app
  // returning to the foreground. Added to fetchData useEffect deps below
  // so the dashboard rehydrates after long background windows. See
  // Resume/Rehydrate Engine architecture (2026-02-28).
  const resumeEpoch = useResumeEpoch();
  const tapEpoch = useTapEpoch();
  const [inquiries, setInquiries] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true); // Start true for initial load skeleton
  const [profileSaved, setProfileSaved] = useState(false);
  
  // NOTE: Silent mount-time role flip removed (Phase 2 of role-mode fix).
  // Cross-role navigation is now handled by the centralized RoleSwitchGuard
  // inside ProtectedRoute in App.js — it explicitly asks the user before
  // changing activeRole, so landing here never silently rewrites role state.
  
  // Tab state - sync with URL params with transition for smoother switching
  const [isPending, startTransition] = useTransition();
  const [activeTab, setActiveTabState] = useState(() => {
    return searchParams.get('tab') || 'welcome';
  });

  // Handle tab changes with URL sync and transition
  const handleTabChange = useCallback((newTab) => {
    startTransition(() => {
      setActiveTabState(newTab);
      setSearchParams({ tab: newTab }, { replace: true });
    });
    // Scroll to top when changing tabs (outside transition for immediate effect)
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [setSearchParams]);

  // Clear tile nav state when switching to Home (bottom nav)
  const handleBottomNav = useCallback((tab) => {
    startTransition(() => {
      setTileNavState(null);
      setActiveTabState(tab);
      setSearchParams({ tab: tab }, { replace: true });
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [setSearchParams]);

  // Also expose setActiveTab for silent auto-switches
  const setActiveTab = useCallback((newTab) => {
    startTransition(() => {
      setActiveTabState(newTab);
    });
    // Scroll to top when changing tabs (outside transition for immediate effect)
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Sync tab state when URL changes (browser back/forward)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTabState(tabFromUrl);
    }
  }, [searchParams, activeTab]);

  // ── Deep-link: auto-open chat thread from ?inquiryId=... ──
  // Audit ref: 2026-06-03 proposal notification deep-link fix.
  // Used by push notifications that target a specific conversation
  // (e.g. notify_proposal_sent → /customer-dashboard?tab=messages&inquiryId=…).
  // Fires once per inquiry-id, only after the inquiry list is loaded so we
  // pass the full inquiry object — not just the id — to handleOpenMessaging
  // (matches the existing call-site contract on lines 2402/2441/2502/2524).
  const autoOpenedInquiryIdRef = useRef(null);
  useEffect(() => {
    const targetInquiryId = searchParams.get('inquiryId');
    if (!targetInquiryId) return;
    if (autoOpenedInquiryIdRef.current === targetInquiryId) return;
    if (!Array.isArray(inquiries) || inquiries.length === 0) return;
    const target = inquiries.find((i) => i.id === targetInquiryId);
    if (!target) return;
    autoOpenedInquiryIdRef.current = targetInquiryId;
    setSelectedInquiryForMessaging(target);
    setShowMessaging(true);
    // Strip the query param so a back-press / close does not re-trigger.
    try {
      const next = new URLSearchParams(searchParams);
      next.delete('inquiryId');
      setSearchParams(next, { replace: true });
    } catch (_) { /* noop — preserves robustness for environments without setSearchParams */ }
  }, [searchParams, inquiries, setSearchParams]);

  const [uploadingImage, setUploadingImage] = useState(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [reportingInquiry, setReportingInquiry] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [updatingPhone, setUpdatingPhone] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [securityAnswers, setSecurityAnswers] = useState([]);
  const [currentPasswordForSecurity, setCurrentPasswordForSecurity] = useState('');
  const [updatingSecurityQuestions, setUpdatingSecurityQuestions] = useState(false);
  
  // Profile state
  const [profileData, setProfileData] = useState({
    display_name: '',
    first_name: '',
    last_name: '',
    city: '',
    state: '',
    phone: '',
    profile_photo: ''
  });

  // Review state
  const [vendorServiceMap, setVendorServiceMap] = useState({});
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [reviewData, setReviewData] = useState({
    rating: 5,
    comment: '',
    images: []
  });
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [uploadingReviewImage, setUploadingReviewImage] = useState(false);
  const [existingReviews, setExistingReviews] = useState([]); // Track which inquiries already have reviews

  // Dispute state
  const [disputingInquiry, setDisputingInquiry] = useState(null);
  const [disputeData, setDisputeData] = useState({
    reason: '',
    description: ''
  });

  // Inquiry notes state
  const [addingCustomerNote, setAddingCustomerNote] = useState(null);
  const [newCustomerNote, setNewCustomerNote] = useState('');

  // Customer Payments state
  const [customerPayments, setCustomerPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Events state
  const [events, setEvents] = useState([]);
  // Phase 9A — set of concierge event ids whose latest brief is sealed.
  // Populated by a fire-and-forget per-event operations fetch on mount;
  // backs the "Brief ready" pill on the dashboard event card.
  const [briefReadyEventIds, setBriefReadyEventIds] = useState(() => new Set());
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [paymentSuccessData, setPaymentSuccessData] = useState(null);
  const [disputes, setDisputes] = useState([]);

  // Hero carousel scroll state
  const heroCarouselRef = useRef(null);
  const [heroScrollState, setHeroScrollState] = useState({ atStart: true, atEnd: false, hasOverflow: false });

  // Next Steps carousel scroll state
  const nextStepsCarouselRef = useRef(null);
  const [nextStepsScrollState, setNextStepsScrollState] = useState({ atStart: true, atEnd: false, hasOverflow: false });
  const [serverNextSteps, setServerNextSteps] = useState([]);

  // Booking and payment state
  const [bookings, setBookings] = useState([]);
  const [showServiceAgreement, setShowServiceAgreement] = useState(false);
  const [showPaymentCheckout, setShowPaymentCheckout] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [creatingBooking, setCreatingBooking] = useState(false);

  // Tile Navigation State system — lightweight navigation hints
  const [tileNavState, setTileNavState] = useState(null); // 'events' | 'unread' | 'bookings' | 'payments-due' | null
  const [paymentsDue, setPaymentsDue] = useState([]);
  const [paymentsDueCount, setPaymentsDueCount] = useState(0);

  // Derived: events-with-bookings count for Bookings tile
  const eventsWithBookingsCount = (() => {
    const cancelledBk = ['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'];
    const ids = new Set(bookings.filter(b => !cancelledBk.includes(b.status)).map(b => b.event_id));
    return events.filter(e => ids.has(e.id)).length;
  })();

  // Messaging state
  const [showMessaging, setShowMessaging] = useState(false);
  const [selectedInquiryForMessaging, setSelectedInquiryForMessaging] = useState(null);

  // ===== Collapsible Section States with Persistence =====
  
  // "How Celey Works" state - smart visibility based on customer activity
  // - Expanded for new customers (no inquiries, no bookings, first 7-14 days)
  // - Collapsed for engaged customers (has inquiries or bookings)
  // - Hidden if dismissed (persists per user)
  const [howCeleyWorksDismissed, setHowCeleyWorksDismissed] = useState(() => {
    return localStorage.getItem('customerHowCeleyWorksDismissed') === 'true';
  });
  const [howCeleyWorksExpanded, setHowCeleyWorksExpanded] = useState(true); // Will be set based on activity
  
  // Platform Updates state - collapsible with persistence
  const [platformUpdatesExpanded, setPlatformUpdatesExpanded] = useState(() => {
    const stored = localStorage.getItem('customerPlatformUpdatesExpanded');
    // Default expanded for new customers (null = not set = new)
    return stored === null ? true : stored === 'true';
  });

  // Handler to dismiss "How Celey Works" permanently
  const dismissHowCeleyWorks = () => {
    setHowCeleyWorksDismissed(true);
    localStorage.setItem('customerHowCeleyWorksDismissed', 'true');
  };

  // Handler to toggle "How Celey Works" expanded/collapsed
  const toggleHowCeleyWorks = () => {
    setHowCeleyWorksExpanded(!howCeleyWorksExpanded);
  };

  // Handler to toggle Platform Updates expanded/collapsed
  const togglePlatformUpdates = () => {
    const newState = !platformUpdatesExpanded;
    setPlatformUpdatesExpanded(newState);
    localStorage.setItem('customerPlatformUpdatesExpanded', newState.toString());
  };

  // Fetch payments on initial load for "Your Activity" section and when payments tab is accessed
  useEffect(() => {
    // Always fetch on initial load for the "Payments Due" count in Your Activity
    if (activeTab === 'welcome' || activeTab === 'customer-payments') {
      fetchCustomerPayments();
    }
  }, [activeTab]);

  // Update "How Celey Works" expanded state based on customer activity
  useEffect(() => {
    // Check if customer is "engaged" (has inquiries or completed bookings)
    const hasInquiries = inquiries.length > 0;
    const hasCompletedBookings = bookings.some(b => b.status === 'completed');
    const isEngaged = hasInquiries || hasCompletedBookings;
    
    // Also consider time-based: expanded for first 7-14 days
    // For now, we'll use inquiry/booking activity as the primary signal
    // If user has no activity, keep expanded; otherwise collapse
    setHowCeleyWorksExpanded(!isEngaged);
  }, [inquiries, bookings]);

  useEffect(() => {
    // Redirect admins to admin dashboard
    if (user?.is_admin) {
      navigate('/admin/dashboard');
      return;
    }
    fetchData();
    fetchSecurityQuestions();
    
    // Check for new signup welcome message
    const isNewSignup = sessionStorage.getItem('celey:new_signup');
    if (isNewSignup) {
      sessionStorage.removeItem('celey:new_signup');
      // Show welcome message after a brief delay so the page loads first
      setTimeout(() => {
        toast.success('Welcome to Celey! Your account is ready. Start browsing service providers!', {
          duration: 5000,
        });
      }, 500);
    }
    
    // Check for payment return
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    console.log('[Payment] URL params - payment:', paymentStatus, 'session_id:', sessionId);
    console.log('[Payment] Token exists:', !!localStorage.getItem('token'));
    
    if (paymentStatus === 'success' && sessionId) {
      console.log('[Payment] Processing successful payment return');
      checkPaymentStatus(sessionId);
      // Navigate to payment success tab with session ID
      handleTabChange('payment-success');
      // Store session ID for fetching payment details
      fetchPaymentBySession(sessionId);
      // Clean URL but keep tab
      window.history.replaceState({}, '', '/customer/dashboard?tab=payment-success');
    } else if (paymentStatus === 'cancelled') {
      console.log('[Payment] Payment was cancelled - customer can retry');
      toast.info('Payment cancelled. You can try again when ready.', {
        duration: 5000,
        description: 'Your payment action is still available in Next Steps'
      });
      // Clean URL
      window.history.replaceState({}, '', '/customer/dashboard');
      // Refresh data to ensure UI shows retry option
      fetchData();
    }
    
    // Auto-refresh every 30 seconds to show new messages/updates
    const intervalId = setInterval(() => {
      fetchData();
    }, 30000); // 30 seconds
    
    return () => clearInterval(intervalId);
  }, [user, navigate, activeRole, resumeEpoch, tapEpoch]); // Re-fetch on role change + on app resume + on push tap

  const fetchSecurityQuestions = async () => {
    try {
      const response = await axios.get(`${API}/auth/security-questions`);
      setSecurityQuestions(response.data.questions);
      setSecurityAnswers(response.data.questions.map(q => ({ question: q, answer: '' })));
    } catch (error) {
      console.error('Failed to load security questions:', error);
    }
  };

  // ── Shared carousel scroll helpers ──
  const calcScrollState = (el) => {
    if (!el) return { atStart: true, atEnd: false, hasOverflow: false };
    const hasOverflow = el.scrollWidth > el.clientWidth + 4;
    const atStart = el.scrollLeft < 8;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    return { atStart, atEnd, hasOverflow };
  };

  const updateHeroScroll = useCallback(() => setHeroScrollState(calcScrollState(heroCarouselRef.current)), []);
  const updateNextStepsScroll = useCallback(() => setNextStepsScrollState(calcScrollState(nextStepsCarouselRef.current)), []);

  // Attach scroll + resize listeners to both carousels
  useEffect(() => {
    const pairs = [
      [heroCarouselRef.current, updateHeroScroll],
      [nextStepsCarouselRef.current, updateNextStepsScroll],
    ];
    const observers = [];
    pairs.forEach(([el, update]) => {
      if (!el) return;
      update();
      el.addEventListener('scroll', update, { passive: true });
      const ro = new ResizeObserver(update);
      ro.observe(el);
      observers.push({ el, update, ro });
    });
    return () => observers.forEach(({ el, update, ro }) => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    });
  }, [updateHeroScroll, updateNextStepsScroll, events]);

  const scrollCarousel = useCallback((ref, dir) => {
    const el = ref.current;
    if (!el) return;
    const card = el.firstElementChild;
    const step = card ? card.offsetWidth + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir === 'right' ? step : -step, behavior: 'smooth' });
  }, []);

  // Desktop click-and-drag scroll for carousels
  const useDragScroll = useCallback((ref) => {
    const el = ref.current;
    if (!el) return;
    let isDown = false, startX = 0, scrollL = 0;
    const onDown = (e) => {
      if (e.button !== 0) return;
      isDown = true;
      startX = e.pageX - el.offsetLeft;
      scrollL = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    };
    const onUp = () => { isDown = false; el.style.cursor = ''; el.style.userSelect = ''; };
    const onMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      el.scrollLeft = scrollL - (x - startX);
    };
    el.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    el.addEventListener('mousemove', onMove);
    return () => {
      el.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      el.removeEventListener('mousemove', onMove);
    };
  }, []);

  useEffect(() => {
    const cleanups = [useDragScroll(heroCarouselRef), useDragScroll(nextStepsCarouselRef)];
    return () => cleanups.forEach(fn => fn && fn());
  }, [useDragScroll, events]);

  // Handle review URL param (from Event Command Center "Leave Review" action)
  useEffect(() => {
    const reviewInquiryId = searchParams.get('review');
    if (reviewInquiryId && inquiries.length > 0) {
      const inq = inquiries.find(i => i.id === reviewInquiryId);
      if (inq) {
        openReviewDialog(inq);
        searchParams.delete('review');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, inquiries]);

  // Fetch customer payments
  const fetchCustomerPayments = async () => {
    setLoadingPayments(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/customer/payments`, { headers });
      setCustomerPayments(response.data);
    } catch (error) {
      console.error('Error fetching customer payments:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  // Fetch payment details
  const fetchPaymentDetails = async (paymentId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/customer/payments/${paymentId}`, { headers });
      setSelectedPayment(response.data);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Failed to load payment details');
    }
  };

  // Fetch payment by session ID (for post-success redirect)
  const fetchPaymentBySession = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/customer/payments/by-session/${sessionId}`, { headers });
      setPaymentSuccessData(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching payment by session:', error);
      return null;
    }
  };

  // Copy receipt details to clipboard
  const copyReceiptDetails = (payment) => {
    if (!payment) return;
    
    const lines = [
      '═══════════════════════════════',
      '        CELEY PAYMENT RECEIPT',
      '═══════════════════════════════',
      '',
      `Provider: ${payment.vendor_name || 'N/A'}`,
      `Service: ${payment.service || 'N/A'}`,
      '',
      '─── Payment Details ───',
      `Amount: $${payment.amount?.toFixed(2) || '0.00'}`,
      `Type: ${payment.payment_type_label || payment.payment_type || 'N/A'}`,
      `Status: ${payment.status_label || payment.status || 'N/A'}`,
    ];
    
    if (payment.paid_at) {
      lines.push(`Date/Time: ${new Date(payment.paid_at).toLocaleString()}`);
    }
    
    if (payment.payment_method) {
      lines.push(`Payment Method: ${payment.payment_method}`);
    }
    
    if (payment.event_date) {
      lines.push(`Event Date: ${formatLongDate(payment.event_date)}`);
    }
    
    lines.push('');
    lines.push('─── Reference IDs ───');
    
    if (payment.transaction_id) {
      lines.push(`Transaction ID: ${payment.transaction_id}`);
    }
    
    if (payment.booking_id) {
      lines.push(`Booking ID: ${payment.booking_id}`);
    }
    
    if (payment.inquiry_id) {
      lines.push(`Inquiry ID: ${payment.inquiry_id}`);
    }
    
    lines.push('');
    lines.push('═══════════════════════════════');
    lines.push('        Powered by Celey');
    lines.push('═══════════════════════════════');
    
    const receiptText = lines.join('\n');
    
    navigator.clipboard.writeText(receiptText).then(() => {
      toast.success('Receipt details copied');
    }).catch(() => {
      toast.error('Failed to copy receipt details');
    });
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // 15 s per-call timeout — guards against iOS WKWebView resume-hang
      // where backgrounded XHRs can stay suspended indefinitely. Axios
      // default is 0/infinite, which leaves skeletons stuck. See audit
      // 2026-02-28 (Issue 1).
      const reqOpts = { headers, timeout: 15000 };

      const [inquiriesRes, statesRes, profileRes, disputesRes, bookingsRes, reviewsRes, eventsRes, nextStepsRes, paymentsDueRes] = await Promise.all([
        axios.get(`${API}/inquiries/customer`, reqOpts),
        axios.get(`${API}/states`, { timeout: 15000 }),
        axios.get(`${API}/customers/me/profile`, reqOpts),
        axios.get(`${API}/disputes/customer`, reqOpts).catch(() => ({ data: [] })),
        // Pass role=customer to get customer bookings for dual-role users
        axios.get(`${API}/bookings?role=customer`, reqOpts).catch(() => ({ data: [] })),
        axios.get(`${API}/reviews/customer`, reqOpts).catch(() => ({ data: [] })),
        axios.get(`${API}/events/me`, reqOpts).catch(() => ({ data: [] })),
        axios.get(`${API}/next-steps/me`, reqOpts).catch(() => ({ data: { next_steps: [] } })),
        axios.get(`${API}/customer/payments-due`, reqOpts).catch(() => ({ data: { payments_due: [], count: 0 } }))
      ]);

      setInquiries(inquiriesRes.data);
      setStates(statesRes.data.states);
      setDisputes(disputesRes.data);
      setBookings(bookingsRes.data);
      setExistingReviews(reviewsRes.data || []);
      setEvents(eventsRes.data || []);
      // Phase 9A — fire-and-forget brief-ready discovery for concierge
      // events. Parallel, owner-scoped, capped to active concierge
      // events. Each operations call uses the customer-fidelity
      // projection so no admin fields leak. Result: a Set of event ids
      // whose latest brief is sealed → rendered as a "Brief ready" pill
      // on the dashboard card.
      try {
        const conciergeActive = (eventsRes.data || []).filter(e =>
          e.planning_style === 'concierge'
          && !['cancelled', 'completed'].includes(e.status)
        );
        if (conciergeActive.length > 0) {
          const probes = await Promise.all(
            conciergeActive.map(e =>
              axios
                .get(`${API}/events/${e.id}/operations`, reqOpts)
                .then(r => {
                  const rail = r.data?.brief_rail || {};
                  return rail.has_brief && rail.latest_status === 'ready' ? e.id : null;
                })
                .catch(() => null),
            ),
          );
          const ready = new Set(probes.filter(Boolean));
          if (ready.size > 0) setBriefReadyEventIds(ready);
        }
      } catch (_) { /* progressive enhancement — silent on failure */ }
      // Phase 8.6 — post-signup adoption redirect: if the customer
      // just completed Concierge signup and they have exactly one
      // pre-graduation Concierge event, route them straight to its
      // Event Home. Flag is one-shot — cleared regardless of redirect.
      // Phase 8.6B (F4) — the prior `inquiries.length === 0` filter
      // collided with the placeholder `celey-concierge` inquiry every
      // intake creates, so the redirect never fired in production. The
      // authoritative pre-graduation signal lives on the Operations
      // projection (ADR I18). EventDetail itself still gates EventHome
      // on `is_pre_graduation === true`, so we can route on the lighter
      // event-list signal here: planning_style=concierge AND status not
      // in {cancelled, completed}. If the event has graduated by the
      // time the customer lands, EventDetail simply renders the legacy
      // layout — the redirect is harmless either way.
      try {
        const justSignedUp = sessionStorage.getItem('celey_concierge_just_signed_up') === '1';
        if (justSignedUp) {
          sessionStorage.removeItem('celey_concierge_just_signed_up');
          const evts = eventsRes.data || [];
          const conciergePre = evts.filter(e =>
            e.planning_style === 'concierge'
            && !['cancelled', 'completed'].includes(e.status)
          );
          if (conciergePre.length === 1) {
            navigate(`/events/${conciergePre[0].id}`, { replace: true });
            return;
          }
        }
      } catch (_) { /* ignore */ }
      setServerNextSteps(nextStepsRes.data?.next_steps || []);
      setPaymentsDue(paymentsDueRes.data?.payments_due || []);
      setPaymentsDueCount(paymentsDueRes.data?.count || 0);

      // Lightweight vendor service lookup for actionable inquiries
      const actionableStatuses = ['accepted', 'agreement_signed', 'proposal_sent', 'deposit_paid', 'in_progress', 'fully_paid', 'final_paid'];
      const vendorIds = [...new Set(
        inquiriesRes.data
          .filter(i => actionableStatuses.includes(i.status))
          .map(i => i.vendor_id)
          .filter(Boolean)
      )];
      if (vendorIds.length > 0) {
        const vendorResults = await Promise.all(
          vendorIds.map(vid => axios.get(`${API}/vendors/${vid}`, { headers }).catch(() => null))
        );
        const svcMap = {};
        vendorResults.forEach(res => {
          if (res?.data) {
            const v = res.data;
            const svc = v.primary_service || (v.categories && v.categories[0]) || null;
            if (svc) svcMap[v.id] = svc;
          }
        });
        setVendorServiceMap(svcMap);
      }
      
      if (profileRes.data) {
        setProfileData({
          display_name: profileRes.data.display_name || '',
          first_name: profileRes.data.first_name || '',
          last_name: profileRes.data.last_name || '',
          city: profileRes.data.city || '',
          state: profileRes.data.state || '',
          phone: profileRes.data.phone || user?.phone || '',
          profile_photo: profileRes.data.profile_photo || ''
        });
      } else if (user) {
        // Pre-fill phone from user account for new customers
        setProfileData(prev => ({
          ...prev,
          phone: user.phone || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Pre-fill phone from user account if fetch fails
      if (user) {
        setProfileData(prev => ({
          ...prev,
          phone: user.phone || ''
        }));
      }
    } finally {
      setLoading(false); // Always clear loading state after fetch
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setProfileSaved(false);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(`${API}/customers/profile`, profileData, { headers });
      
      // Show success state
      setProfileSaved(true);
      toast.success('✅ Profile updated successfully!');
      
      // Reset success state after 3 seconds
      setTimeout(() => setProfileSaved(false), 3000);
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Create preview URL and show crop dialog
    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result);
      setShowCropDialog(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCroppedImage = async (croppedImageBlob) => {
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const formData = new FormData();
      formData.append('file', croppedImageBlob, 'profile-photo.jpg');

      const response = await axios.post(`${API}/upload/profile?as=customer`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update local state
      const newProfileData = { ...profileData, profile_photo: response.data.url };
      setProfileData(newProfileData);
      
      // Auto-save to backend
      await axios.put(`${API}/customers/profile`, newProfileData, { headers });
      
      toast.success('Profile photo updated successfully!');
      fetchData(); // Refresh to get latest data
      // Notify the Navigation header avatar to refetch immediately so the
      // uploaded photo appears without requiring a route change.
      try {
        window.dispatchEvent(new CustomEvent('celey:profile-photo-updated'));
      } catch (_) { /* CustomEvent unsupported — header will refresh on next nav */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const openReviewDialog = (inquiry) => {
    setSelectedInquiry(inquiry);
    setReviewData({ rating: 5, comment: '', images: [] });
    setShowReviewDialog(true);
  };

  // Handle review image upload
  const handleReviewImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG or PNG image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    if (reviewData.images.length >= 3) {
      toast.error('Maximum 3 images allowed');
      return;
    }

    setUploadingReviewImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setReviewData(prev => ({
        ...prev,
        images: [...prev.images, response.data.url]
      }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingReviewImage(false);
      e.target.value = '';
    }
  };

  // Remove review image
  const removeReviewImage = (index) => {
    setReviewData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Get full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API}${url}`;
  };

  const submitReview = async () => {
    if (!reviewData.comment.trim()) {
      toast.error('Please write a comment for your review');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.post(
        `${API}/reviews`,
        {
          vendor_id: selectedInquiry.vendor_id,
          inquiry_id: selectedInquiry.id,
          rating: reviewData.rating,
          comment: reviewData.comment,
          images: reviewData.images || []
        },
        { headers }
      );

      toast.success('Review submitted! It will be visible after admin approval.');
      setShowReviewDialog(false);
      setSelectedInquiry(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const submitCustomerNote = async (inquiryId) => {
    if (!newCustomerNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/inquiries/${inquiryId}/progress-notes`,
        {
          note: newCustomerNote,
          author_role: 'customer'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(TOAST.NOTE_ADDED);
      setAddingCustomerNote(null);
      setNewCustomerNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    }
  };

  const submitDispute = async () => {
    if (!disputeData.reason || !disputeData.description.trim()) {
      toast.error('Please select a reason and provide a description');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.post(
        `${API}/disputes`,
        {
          inquiry_id: disputingInquiry.id,
          reason: disputeData.reason,
          description: disputeData.description
        },
        { headers }
      );

      toast.success('Dispute submitted! An admin will review your request.');
      setDisputingInquiry(null);
      setDisputeData({ reason: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit dispute');
    } finally {
      setLoading(false);
    }
  };

  const updateEmail = async () => {
    if (!newEmail.trim() || !newEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (newEmail === user?.email) {
      toast.error('New email is the same as current email');
      return;
    }

    if (!window.confirm(`Change your email to ${newEmail}? You'll need to use this email to login.`)) {
      return;
    }

    setUpdatingEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/auth/update-email`,
        { new_email: newEmail },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(TOAST.EMAIL_UPDATED);
      setNewEmail('');
      // Update local user state
      const updatedUser = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      login(updatedUser.data, token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setUpdatingEmail(false);
    }
  };

  const handleUpdatePhone = async () => {
    // Validation
    if (!newPhone.trim()) {
      toast.error('Please enter a valid phone number');
      return;
    }

    if (newPhone === user?.phone) {
      toast.error('This is already your current phone number');
      return;
    }

    if (!window.confirm(`Change your phone number to ${newPhone}?`)) {
      return;
    }

    setUpdatingPhone(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/auth/update-phone`,
        { new_phone: newPhone },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(TOAST.PHONE_NUMBER_UPDATED);
      setNewPhone('');
      // Update local user state
      const updatedUser = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      login(updatedUser.data, token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update phone number');
    } finally {
      setUpdatingPhone(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill all password fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 10) {
      toast.error('New password must be at least 10 characters');
      return;
    }

    setUpdatingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleSecurityQuestionsUpdate = async (e) => {
    e.preventDefault();

    if (!currentPasswordForSecurity) {
      toast.error('Please enter your current password');
      return;
    }

    const filledAnswers = securityAnswers.filter(a => a.answer.trim() !== '');
    if (filledAnswers.length !== 3) {
      toast.error('Please answer all 3 security questions');
      return;
    }

    setUpdatingSecurityQuestions(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/update-security-questions`,
        {
          current_password: currentPasswordForSecurity,
          security_answers: securityAnswers
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(TOAST.SECURITY_QUESTIONS_UPDATED);
      setCurrentPasswordForSecurity('');
      setSecurityAnswers(securityQuestions.map(q => ({ question: q, answer: '' })));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update security questions');
    } finally {
      setUpdatingSecurityQuestions(false);
    }
  };

  // Messaging handlers
  const [scrollToPayment, setScrollToPayment] = useState(false);
  const handleOpenMessaging = (inquiry, { focusPayment = false } = {}) => {
    setScrollToPayment(focusPayment);
    setSelectedInquiryForMessaging(inquiry);
    setShowMessaging(true);
  };

  // Memoized close handler to prevent unnecessary re-renders in MessagingThread
  const handleCloseMessaging = useCallback(() => {
    setShowMessaging(false);
    setScrollToPayment(false);
    // Refresh data so unread counts and stats update after reading messages
    fetchData();
  }, []);

  const handleProposalAccepted = async (booking) => {
    // Proposal accepted and booking created
    setBookings([...bookings, booking]);
    setSelectedBooking(booking);
    setShowMessaging(false);
    
    // In the optimized flow, agreements are pre-accepted
    // Go directly to payment checkout if agreement already signed
    if (booking.service_agreement_accepted_by_customer && booking.service_agreement_accepted_by_vendor) {
      setShowPaymentCheckout(true);
    } else {
      // Legacy flow: Show service agreement first
      setShowServiceAgreement(true);
    }
    
    await fetchData();
  };
  
  const handleBookingCancelled = async (cancelledBooking) => {
    // Update bookings state with cancelled booking
    setBookings(bookings.map(b => 
      b.id === cancelledBooking.id ? cancelledBooking : b
    ));
    // Refresh all data to get updated inquiry status
    await fetchData();
  };

  // Booking and payment handlers
  const handleCreateBooking = async (inquiryId) => {
    setCreatingBooking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/bookings`,
        { inquiry_id: inquiryId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const booking = response.data;
      setBookings([...bookings, booking]);
      setSelectedBooking(booking);
      
      // Show service agreement dialog
      setShowServiceAgreement(true);
      
      toast.success('Booking created! Please review the service agreement.');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setCreatingBooking(false);
    }
  };

  const handleServiceAgreementAccepted = async () => {
    // After agreement accepted, check if both parties accepted
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/bookings/${selectedBooking.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const updatedBooking = response.data;
      setSelectedBooking(updatedBooking);
      
      // If both parties accepted, show payment checkout immediately
      if (updatedBooking.service_agreement_accepted_by_customer && 
          updatedBooking.service_agreement_accepted_by_vendor) {
        setShowPaymentCheckout(true);
        toast.success('Agreement accepted! Please pay the deposit to secure your booking.');
      } else {
        // Customer accepted first, waiting for vendor
        toast.success('Agreement signed! The service provider will be notified and can begin work once they sign as well.');
      }
      
      await fetchData();
    } catch (error) {
      console.error('Error checking booking status:', error);
    }
  };

  const getBookingForInquiry = (inquiryId) => {
    return bookings.find(b => b.inquiry_id === inquiryId);
  };

  const checkPaymentStatus = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    const poll = async () => {
      if (attempts >= maxAttempts) {
        toast.error('Payment verification timed out. Refreshing page...');
        // Force refresh after timeout
        setTimeout(() => fetchData(), 1000);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/payments/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Thank you.');
          await fetchData();
          return;
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired.');
          await fetchData();
          return;
        }

        // Continue polling
        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        // Don't show error toast on every poll, just log it
        if (attempts >= maxAttempts - 1) {
          toast.warning('Could not verify payment status automatically. Please refresh the page to see updated status.');
        }
        // Force refresh data even if verification fails
        setTimeout(() => fetchData(), 2000);
      }
    };

    toast.info('Verifying payment...');
    poll();
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const completedInquiries = inquiries.filter(inq => inq.status === 'completed');
  
  // Filter out inquiries that already have reviews
  const reviewableInquiries = completedInquiries.filter(inq => 
    !existingReviews.some(review => review.inquiry_id === inq.id)
  );
  
  // Get inquiries that already have reviews (for display)
  const reviewedInquiries = completedInquiries.filter(inq => 
    existingReviews.some(review => review.inquiry_id === inq.id)
  );

  return (
    <div className="min-h-screen-safe bg-[#F7F7F5] pb-20 md:pb-0 flex flex-col">
      {/* Header */}
      <Navigation showFeedback={true} hideMobileNav={true} />

      {/* Responsive container - wider on tablets/desktops */}
      <div className="w-full max-w-screen-2xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-3 sm:py-4 md:py-8 flex-1">
        {/* Dynamic header — hidden on mobile (bottom tab provides context), visible on desktop */}
        {!['welcome', 'profile', 'profile-edit', 'profile-security', 'help'].includes(activeTab) && (
          <div className="hidden md:block mb-4 sm:mb-6 md:mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-1 sm:mb-2">
              {activeTab === 'inquiries' && 'My Bookings'}
              {activeTab === 'messages' && 'Chats'}
              {activeTab === 'reviews' && 'Reviews'}
              {activeTab === 'events' && 'My Events'}
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              {activeTab === 'inquiries' && 'Track your service requests and bookings'}
              {activeTab === 'messages' && 'Conversations with service providers'}
              {activeTab === 'reviews' && 'Leave reviews for completed services'}
            </p>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={(tab) => { setTileNavState(null); handleTabChange(tab); }} className="w-full space-y-4 sm:space-y-6">
          {/* Tab Navigation - Hidden on mobile (using bottom bar instead), visible on desktop */}
          <div className="relative hidden md:block">
            <div className="overflow-x-auto scrollbar-hide" onClickCapture={() => setTileNavState(null)}>
              <TabsList className="inline-flex min-w-full sm:min-w-0 sm:w-full justify-start sm:justify-center bg-gray-100/80 p-1 rounded-lg">
              <TabsTrigger value="welcome" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <Home className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Home</span>
                <span className="sm:hidden">Home</span>
              </TabsTrigger>
              <TabsTrigger value="messages" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" />
                Chats
                {(() => {
                  const unreadCount = inquiries.filter(inq => 
                    (inq.unread_count > 0) && 
                    inq.status !== 'cancelled' && 
                    inq.status !== 'completed'
                  ).length;
                  return unreadCount > 0 && <Badge className="ml-0.5 bg-emerald-500 text-[10px] px-1.5 py-0">{unreadCount}</Badge>;
                })()}
              </TabsTrigger>
              <TabsTrigger value="events" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <PartyPopper className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">My Events</span>
                <span className="sm:hidden">Events</span>
                {(() => {
                  // Count events with actionable vendors (proposals, payments due)
                  const actionableStatuses = ['proposal_sent', 'proposal_received'];
                  const actionableEventIds = new Set();
                  inquiries.forEach(inq => {
                    if (actionableStatuses.includes(inq.status) && inq.event_id) {
                      actionableEventIds.add(inq.event_id);
                    }
                    // Also count deposit/final payment pending
                    if (['accepted', 'deposit_paid'].includes(inq.status) && inq.event_id) {
                      actionableEventIds.add(inq.event_id);
                    }
                  });
                  return actionableEventIds.size > 0 && <Badge className="ml-0.5 bg-emerald-500 text-[10px] px-1.5 py-0">{actionableEventIds.size}</Badge>;
                })()}
              </TabsTrigger>
              <TabsTrigger value="reviews" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <Star className="w-3.5 h-3.5" />
                Reviews
                {reviewableInquiries.length > 0 && <Badge className="ml-0.5 bg-violet-500 text-[10px] px-1.5 py-0">{reviewableInquiries.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="profile" className="text-xs sm:text-sm whitespace-nowrap flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                Account
              </TabsTrigger>
            </TabsList>
            </div>
          </div>

          {/* Welcome Tab — Hero-First Premium Home */}
          <TabsContent value="welcome">
            <div className="pb-4">
              {loading ? (
                /* ── Loading skeleton: prevents false empty-state flash ── */
                <div className="space-y-10 animate-in fade-in-0 duration-300">
                  <div className="px-1">
                    <div className="h-8 w-56 bg-gray-100 rounded-lg animate-pulse" />
                    <div className="h-5 w-40 bg-gray-50 rounded-md animate-pulse mt-2" />
                  </div>
                  <div className="rounded-[20px] border border-[#E6E6E0] bg-white p-6 space-y-4">
                    <div className="flex gap-4 overflow-hidden">
                      {[1,2].map(i => (
                        <div key={i} className="shrink-0 w-[85%] sm:w-[70%] md:w-[48%] max-w-[420px] rounded-[20px] bg-gray-50 animate-pulse" style={{height: 200}} />
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[1,2,3,4].map(i => <div key={i} className="h-20 rounded-2xl bg-gray-50 animate-pulse" />)}
                  </div>
                </div>
              ) : (() => {
                // ═══ Helpers ═══
                const getEventName = (inq) => {
                  if (inq.event_id) {
                    const evt = events.find(e => e.id === inq.event_id);
                    if (evt?.event_type) return evt.event_type;
                  }
                  return inq.event_type || 'your event';
                };

                // ═══ Mood system sourced from eventThemeService ═══

                // ═══ Action items from server-side Next Steps System ═══
                const STEP_ICONS = {
                  review_proposal: <ClipboardList className="w-5 h-5 text-indigo-600" />,
                  pay_deposit: <CreditCard className="w-5 h-5 text-amber-600" />,
                  deposit_expired: <MessageSquare className="w-5 h-5 text-red-600" />,
                  pay_final: <CreditCard className="w-5 h-5 text-amber-600" />,
                  final_overdue: <MessageSquare className="w-5 h-5 text-red-600" />,
                  leave_review: <Star className="w-5 h-5 text-violet-500" />,
                  reply_to_vendor: <MessageSquare className="w-5 h-5 text-emerald-600" />,
                  waiting_vendor_response: <Clock className="w-5 h-5 text-gray-400" />,
                  waiting_change_response: <Clock className="w-5 h-5 text-amber-500" />,
                };
                const STEP_ACCENTS = {
                  review_proposal: 'bg-indigo-50/20 border-indigo-100/50',
                  pay_deposit: 'bg-amber-50/20 border-amber-100/50',
                  deposit_expired: 'bg-red-50/20 border-red-100/50',
                  pay_final: 'bg-amber-50/20 border-amber-100/50',
                  final_overdue: 'bg-red-50/20 border-red-100/50',
                  leave_review: 'bg-violet-50/20 border-violet-100/50',
                  reply_to_vendor: 'bg-emerald-50/20 border-emerald-100/50',
                  waiting_vendor_response: 'bg-gray-50/30 border-gray-200/40',
                  waiting_change_response: 'bg-amber-50/20 border-amber-100/50',
                };
                const STEP_CTAS = {
                  review_proposal: 'Review Proposal',
                  pay_deposit: 'Pay Deposit',
                  deposit_expired: 'Message Vendor',
                  pay_final: 'Complete Payment',
                  final_overdue: 'Message Vendor',
                  leave_review: 'Leave Review',
                  reply_to_vendor: 'Reply',
                };

                const actionItems = serverNextSteps
                  .filter(s => s.action_type !== 'passive')
                  .map(step => {
                    const type = step.step_key.split(':')[0];
                    const ctx = step.context;
                    const inq = inquiries.find(i => i.id === ctx.inquiry_id);
                    const bk = ctx.booking_id ? bookings.find(b => b.id === ctx.booking_id) : null;
                    const isUrgent = step.urgency === 'critical' || step.urgency === 'warning';

                    // Fallback: navigate to event page if specific action can't be resolved
                    const fallback = ctx.event_id ? () => navigate(`/events/${ctx.event_id}`) : undefined;

                    let action;
                    switch (step.action_type) {
                      case 'navigate_to_chat':
                        action = inq ? () => handleOpenMessaging(inq) : fallback;
                        break;
                      case 'pay_deposit':
                      case 'pay_final':
                        action = bk ? () => { setSelectedBooking(bk); setShowPaymentCheckout(true); } : fallback;
                        break;
                      case 'leave_review':
                        action = inq ? () => openReviewDialog(inq) : fallback;
                        break;
                      default:
                        action = inq ? () => handleOpenMessaging(inq) : fallback;
                    }

                    return {
                      id: ctx.inquiry_id || ctx.booking_id || step.step_key,
                      eventId: ctx.event_id,
                      priority: step.priority,
                      title: step.label,
                      detail: step.description,
                      cta: STEP_CTAS[type] || 'View',
                      action,
                      accent: isUrgent ? 'bg-red-50/20 border-red-100/50' : (STEP_ACCENTS[type] || 'bg-gray-50/30 border-gray-200/40'),
                      icon: isUrgent ? <CreditCard className="w-5 h-5 text-red-600" /> : (STEP_ICONS[type] || <CheckCircle className="w-5 h-5 text-gray-500" />),
                    };
                  });

                // Build balanced display set
                const displayItems = actionItems.slice(0, 8);

                // ═══ Upcoming events — filtered & scored for hero ═══
                // B-EXP-6.23 — closed Concierge cases have graduated
                // and MUST NOT surface as upcoming ("Your next event is
                // coming up" / "Upcoming" hero card). Excluded here so
                // the graduated case falls through to the calmer Past
                // Events section already surfaced by completedEvents.
                const now = new Date();
                const allUpcoming = events
                  .filter(e => !['completed', 'cancelled', 'closed'].includes(e.status))
                  .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

                // Score each event by backend status (single source of truth)
                const scoreEvent = (evt) => {
                  if (evt.status === 'final') return 4;
                  if (evt.status === 'booked') return 3;
                  if (evt.status === 'planning') return 2;
                  return 1; // draft
                };

                // Hero: higher status first
                const nonDraft = allUpcoming.filter(e => scoreEvent(e) >= 2);
                const heroEvents = nonDraft.length > 0
                  ? [...nonDraft].sort((a, b) => scoreEvent(b) - scoreEvent(a) || new Date(a.event_date) - new Date(b.event_date))
                  : allUpcoming.slice(0, 2); // fallback: show first 2 drafts if nothing else

                // ═══ Smart Greeting ═══
                const getGreeting = () => {
                  const hour = new Date().getHours();
                  const firstName = user?.first_name || user?.name?.split(' ')[0] || user?.display_name?.split(' ')[0] || '';
                  const name = firstName ? `, ${firstName}` : '';
                  if (hour < 12) return `Good morning${name}`;
                  if (hour < 17) return `Good afternoon${name}`;
                  return `Good evening${name}`;
                };

                // ── Deterministic, state-aware hero subtext ladder ──
                // Customer side leans warmer than vendor (planning-oriented,
                // less operational). Priority order is strict — first match
                // wins, no randomness, no rotation. Sources are already in
                // scope (inquiries, heroEvents, events) so this can never
                // disagree with the rest of the dashboard.
                const unreadFromVendors = (inquiries || []).filter(i => (i.unread_count || 0) > 0).length;
                const hasActivePlanning = heroEvents.length > 0;
                let supportLine;
                // P1 — unread / vendor activity
                if (unreadFromVendors > 0) supportLine = 'You have vendor updates waiting';
                // P2 — active planning
                else if (hasActivePlanning) supportLine = 'Your next event is coming up';
                else if (events.length > 0) supportLine = 'Everything for your event, in one place';
                // P3 — idle
                else supportLine = 'Start planning your next event';

                // ═══ Hero state ═══
                // STATE A: No events at all
                // STATE B: Has active upcoming events (planning)
                // STATE C: Has events but none upcoming/active (completed/idle)
                const heroState = events.length === 0 
                  ? 'A' 
                  : heroEvents.length > 0 
                    ? 'B' 
                    : 'C';

                // ═══ Render ═══
                return (
                  <div className="space-y-10 animate-in fade-in-0 duration-300">

                    {/* ═══ Keyframes for hero micro-interactions ═══ */}
                    <style>{`
                      @keyframes ctaBreathe {
                        0%, 100% { transform: scale(1); }
                        50% { transform: scale(1.02); }
                      }
                      @keyframes ctaGlow {
                        0%, 100% { opacity: 0.25; transform: scale(1); }
                        50% { opacity: 0.5; transform: scale(1.05); }
                      }
                      @keyframes ctaShimmer {
                        0%, 88%, 100% { background-position: -200% center; }
                        94% { background-position: 200% center; }
                      }
                      @keyframes iconLife {
                        0%, 85%, 100% { transform: scale(1); }
                        90% { transform: scale(1.06); }
                        95% { transform: scale(0.98); }
                      }
                      @keyframes sparkCue {
                        0%, 100% { opacity: 0; transform: translate(0, 0); }
                        15% { opacity: 0.45; transform: translate(1px, -1px); }
                        50% { opacity: 0.3; transform: translate(-1px, 1px); }
                        85% { opacity: 0.45; transform: translate(1px, 0); }
                      }
                      .hero-cta {
                        animation: ctaBreathe 3s ease-in-out infinite;
                        position: relative;
                        overflow: hidden;
                      }
                      .hero-cta::after {
                        content: '';
                        position: absolute;
                        inset: 0;
                        background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.25) 50%, transparent 60%);
                        background-size: 200% 100%;
                        animation: ctaShimmer 8s ease-in-out infinite;
                        pointer-events: none;
                        border-radius: inherit;
                      }
                      .hero-cta:hover, .hero-cta:focus {
                        animation: none;
                        transform: scale(1.03);
                        transition: transform 0.15s ease;
                      }
                      .hero-cta:active {
                        animation: none;
                        transform: scale(0.97);
                        transition: transform 0.08s ease;
                      }
                      .hero-cta:hover::after, .hero-cta:focus::after {
                        animation: none;
                        opacity: 0;
                      }
                      .hero-icon-pulse {
                        animation: iconLife 7s ease-in-out infinite;
                      }
                      .hero-cta-glow {
                        animation: ctaGlow 3s ease-in-out infinite;
                      }
                      .hero-spark {
                        animation-name: sparkCue;
                        animation-timing-function: ease-in-out;
                        animation-iteration-count: infinite;
                        animation-fill-mode: both;
                      }
                    `}</style>

                    {/* ═══ Smart Greeting ═══ */}
                    <div className="px-1" data-testid="smart-greeting">
                      <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1C1C] tracking-tight" data-testid="greeting-text">
                        {getGreeting()}
                      </h1>
                      <p className="text-sm sm:text-base text-[#9A9A94] mt-1" data-testid="greeting-subtext">
                        {supportLine}
                      </p>
                    </div>

                    {/* ═══ State-Based Hero Card ═══ */}
                    {(heroState === 'A' || heroState === 'C') && (
                      <div 
                        className="relative overflow-hidden rounded-[20px] bg-white border border-[#E6E6E0] p-8 sm:p-10 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]" 
                        data-testid={heroState === 'A' ? 'hero-state-a' : 'hero-state-c'}
                      >
                        {/* Distributed ambient accents — soft sparkle field */}
                        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                          {/* Near icon, upper-left quadrant */}
                          <div className="hero-spark absolute rounded-full bg-[#C6A96B]" style={{width:4,height:4,top:'14%',left:'32%',animationDuration:'7s',animationDelay:'0s'}} />
                          {/* Upper-right area, balancing the icon */}
                          <div className="hero-spark absolute rounded-full bg-[#D4B87A]" style={{width:3,height:3,top:'18%',right:'28%',animationDuration:'8.5s',animationDelay:'2.5s'}} />
                          {/* Mid-left, near body copy */}
                          <div className="hero-spark absolute rounded-full bg-[#C6A96B]" style={{width:3.5,height:3.5,top:'50%',left:'16%',animationDuration:'6.5s',animationDelay:'4s'}} />
                          {/* Mid-right, near body copy */}
                          <div className="hero-spark absolute rounded-full bg-[#D4B87A]" style={{width:3,height:3,top:'48%',right:'18%',animationDuration:'9s',animationDelay:'1.5s'}} />
                          {/* Lower area, flanking CTA but not crowding */}
                          <div className="hero-spark absolute rounded-full bg-[#C6A96B]" style={{width:3.5,height:3.5,bottom:'18%',left:'26%',animationDuration:'7.5s',animationDelay:'3.2s'}} />
                        </div>
                        {/* Icon with subtle life pulse */}
                        <div className="hero-icon-pulse w-14 h-14 mx-auto mb-5 bg-[#F5F0E8] rounded-2xl flex items-center justify-center relative z-[1]">
                          <PartyPopper className="w-7 h-7 text-[#C6A96B]" />
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-[#1C1C1C] tracking-tight mb-2 relative z-[1]">
                          {heroState === 'A' ? 'Your celebration starts here' : 'Plan your next celebration'}
                        </h2>
                        <p className="text-[#9A9A94] text-base max-w-md mx-auto mb-8 leading-relaxed relative z-[1]">
                          {heroState === 'A' 
                            ? 'Plan your event, connect with providers, and bring it to life \u2014 all in one place'
                            : 'Discover providers and start your next event in minutes'}
                        </p>
                        {/* CTA with glow and breathing */}
                        <div className="relative inline-block z-[1]">
                          <div className="hero-cta-glow absolute -inset-3 rounded-full bg-[#C6A96B]/20 blur-lg pointer-events-none" aria-hidden="true" />
                          <Button
                            onClick={() => navigate('/plan-event')}
                            className="hero-cta bg-[#C6A96B] hover:bg-[#B89655] text-white px-10 py-3.5 rounded-full font-semibold shadow-sm relative z-[1]"
                            data-testid={heroState === 'A' ? 'start-my-event-btn' : 'dashboard-plan-event-btn'}
                          >
                            {heroState === 'A' ? 'Start My Event' : 'Start Planning'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* ═══ 1. HERO EVENT CAROUSEL (ALWAYS FIRST) ═══ */}
                    {heroEvents.length > 0 && (
                      <div data-testid="hero-events-section">
                        <p className="text-[11px] font-medium text-[#9A9A94] uppercase tracking-wider px-1 mb-3" data-testid="hero-section-label">Your Events</p>
                        <div className="relative group/carousel">
                          {/* Left arrow */}
                          {heroScrollState.hasOverflow && !heroScrollState.atStart && (
                            <button
                              data-testid="hero-carousel-prev"
                              onClick={() => scrollCarousel(heroCarouselRef, 'left')}
                              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-white/90 border border-slate-200 shadow-md hover:bg-white hover:shadow-lg transition-all -ml-1"
                              aria-label="Previous events"
                            >
                              <ChevronLeft className="w-5 h-5 text-slate-600" />
                            </button>
                          )}
                          {/* Right arrow */}
                          {heroScrollState.hasOverflow && !heroScrollState.atEnd && (
                            <button
                              data-testid="hero-carousel-next"
                              onClick={() => scrollCarousel(heroCarouselRef, 'right')}
                              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-white/90 border border-slate-200 shadow-md hover:bg-white hover:shadow-lg transition-all -mr-1"
                              aria-label="Next events"
                            >
                              <ChevronRight className="w-5 h-5 text-slate-600" />
                            </button>
                          )}
                          {/* Edge fade indicators */}
                          {heroScrollState.hasOverflow && !heroScrollState.atStart && (
                            <div className="absolute left-0 top-0 bottom-2 w-8 bg-gradient-to-r from-white/80 to-transparent z-[5] pointer-events-none rounded-l-[20px]" />
                          )}
                          {heroScrollState.hasOverflow && !heroScrollState.atEnd && (
                            <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-white/80 to-transparent z-[5] pointer-events-none rounded-r-[20px]" />
                          )}
                          <div
                            ref={heroCarouselRef}
                            className={`flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-1 px-1 ${heroEvents.length <= 3 ? 'md:justify-center' : ''} ${heroEvents.length === 1 ? 'justify-center' : ''}`}
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                            data-testid="hero-carousel"
                          >
                          {heroEvents.map((evt) => {
                            const evtDate = new Date(evt.event_date);
                            const hasEvtDate = Boolean(evt.event_date) && !isNaN(evtDate.getTime());
                            const daysUntil = Math.max(0, getDaysUntilEvent(evt.event_date));
                            const vendorCount = inquiries.filter(i => i.event_id === evt.id && !['cancelled', 'declined'].includes(i.status)).length;
                            const bookedCount = evt.vendors_booked || 0;
                            const evtScore = scoreEvent(evt);
                            const theme = getEventTheme(evt.event_type);
                            const { mood, decoVariant } = theme;
                            const decoFlags = theme.decoFlags;
                            const moodStyle = { gradient: theme.gradient, countdownBg: theme.countdownBg, cardBorder: theme.cardBorder };

                            // Status badge — subtle premium treatment
                            let badgeLabel, badgeStyle;
                            if (evt.fully_booked_celebrated) {
                              badgeLabel = 'Fully Booked';
                              badgeStyle = 'text-[#5A8A6F] bg-[#8FAF9F]/15';
                            } else if (evtScore === 3) {
                              badgeLabel = bookedCount >= vendorCount && vendorCount > 0 ? 'Fully Booked' : 'Confirmed';
                              badgeStyle = 'text-[#5A8A6F] bg-[#8FAF9F]/15';
                            } else if (evtScore === 2) {
                              badgeLabel = vendorCount > 0 ? `${vendorCount} vendor${vendorCount > 1 ? 's' : ''} contacted` : 'Vendors Contacted';
                              badgeStyle = 'text-[#C6A96B] bg-[#C6A96B]/10';
                            } else {
                              badgeLabel = 'Upcoming';
                              badgeStyle = 'text-[#9A9A94] bg-[#E6E6E0]';
                            }

                            // Variant-specific badge tinting (from eventThemeService)
                            if (decoVariant) badgeStyle = tintBadgeStyle(badgeStyle, decoVariant);

                            return (
                              <div
                                key={evt.id}
                                className={`snap-start shrink-0 flex flex-col rounded-[20px] overflow-hidden cursor-pointer border transition-transform duration-100 active:scale-[0.98] ${moodStyle.cardBorder} ${heroEvents.length === 1 ? 'w-full max-w-[300px] mx-auto' : 'w-[80%] sm:w-[60%] md:w-[32%] lg:w-[28%] max-w-[300px]'}`}
                                onClick={() => navigate(`/events/${evt.id}`)}
                                data-testid={`hero-card-${evt.id}`}
                                data-mood={mood}
                                data-deco-variant={decoVariant || undefined}
                                style={{
                                  boxShadow: '0 10px 40px -10px rgba(150,130,95,0.20), 0 4px 12px -4px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(198,169,107,0.10)',
                                  animation: 'heroLift 400ms ease-out both',
                                }}
                              >
                                <div
                                  className="flex flex-col flex-1 p-5 pb-4 relative"
                                  style={{
                                    background: decoVariant ? DECO_GRADIENTS[decoVariant] : moodStyle.gradient,
                                    minHeight: '220px',
                                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65), inset 0 -1px 0 rgba(198,169,107,0.06)',
                                  }}
                                >
                                  {/* Celebration decorations — surface-aware (subtle on Home) */}
                                  <EventDecorations decoVariant={decoVariant} decoFlags={decoFlags} eventId={evt.id} surface="home" />
                                  {/* Zone 1: Status badge + Countdown (fixed height row) */}
                                  <div className="flex items-start justify-between gap-3 mb-2 min-h-[36px]">
                                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-3.5 py-1.5 rounded-full backdrop-blur-sm ${badgeStyle}`} style={{ boxShadow: '0 1px 4px -1px rgba(0,0,0,0.06)' }}>
                                      {evtScore === 3 && <Sparkles className="w-3 h-3" />}
                                      {badgeLabel}
                                    </span>
                                    <div className={`text-center shrink-0 ${moodStyle.countdownBg} rounded-2xl px-4 py-2.5 min-w-[60px]`} style={{ boxShadow: '0 2px 8px -2px rgba(0,0,0,0.08), 0 0 0 0.5px rgba(198,169,107,0.08)' }}>
                                      {!hasEvtDate ? (
                                        <p className="text-xs font-bold text-[#9A9A94] py-1" data-testid={`hero-countdown-noset-${evt.id}`}>
                                          Date not set
                                        </p>
                                      ) : daysUntil <= 365 ? (
                                        <>
                                          <p className="text-2xl font-bold text-[#1C1C1C] leading-none">{daysUntil}</p>
                                          <p className="text-[9px] font-bold text-[#9A9A94] uppercase tracking-wider mt-0.5">
                                            {daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Day' : 'Days'}
                                          </p>
                                        </>
                                      ) : (
                                        <p className="text-xs font-bold text-[#9A9A94] py-1">
                                          {evtDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  {/* Zone 2: Title (clamped 2 lines, fixed space) */}
                                  <h2 className="text-xl sm:text-2xl font-bold text-[#1C1C1C] tracking-tight leading-tight line-clamp-2 min-h-[44px]">
                                    {(evt.title && String(evt.title).trim()) || evt.event_type || 'Event'}
                                  </h2>

                                  {/* Phase 9A — Brief ready pill on Home hero card. */}
                                  {evt.planning_style === 'concierge'
                                    && !['cancelled', 'completed'].includes(evt.status)
                                    && briefReadyEventIds && briefReadyEventIds.has(evt.id) && (
                                    <span
                                      className="mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium self-start"
                                      data-testid={`event-card-brief-ready-pill-${evt.id}`}
                                    >
                                      Brief ready
                                    </span>
                                  )}

                                  {/* Zone 3: Metadata (always reserves space) */}
                                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-[#9A9A94] mt-3 min-h-[24px]">
                                    <span className="flex items-center gap-1.5">
                                      <Calendar className="w-3.5 h-3.5 text-[#B5B5AD]" />
                                      {hasEvtDate
                                        ? evtDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                                        : 'Date not set'}
                                    </span>
                                    {(evt.location_city || evt.location_zip) && (
                                      <span className="flex items-center gap-1.5">
                                        <MapPin className="w-3.5 h-3.5 text-[#B5B5AD]" />
                                        {formatEventLocation(evt) || evt.location_zip}
                                      </span>
                                    )}
                                  </div>

                                  {/* Zone 4: CTA (pinned to bottom) */}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); navigate(`/events/${evt.id}`); }}
                                    className="w-full text-white font-semibold text-sm py-2.5 rounded-full hover:brightness-110 transition-all duration-200 active:scale-[0.98] mt-auto pt-2"
                                    style={{
                                      background: 'linear-gradient(165deg, #DECE96 0%, #C9AD6E 35%, #B89655 100%)',
                                      boxShadow: '0 6px 20px -4px rgba(184,150,85,0.40), 0 2px 6px -2px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -1px 0 rgba(0,0,0,0.05)',
                                    }}
                                    data-testid={`hero-view-event-${evt.id}`}
                                  >
                                    View Event
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        </div>

                        
                      </div>
                    )}

                    {/* ═══ 2. Stats Row (global summary tiles) ═══ */}
                    {events.length > 0 && (
                      <div className="grid grid-cols-4 gap-2.5" data-testid="stats-row">
                        {[
                          {
                            count: events.filter(e => e.status !== 'cancelled').length,
                            label: 'Events',
                            bg: 'from-emerald-50/70 to-white border-emerald-100/60',
                            onClick: () => { setTileNavState('events'); handleTabChange('events'); },
                          },
                          {
                            count: inquiries.filter(i => i.unread_count > 0).length,
                            label: 'Unread',
                            bg: 'from-purple-50/70 to-white border-purple-100/60',
                            onClick: () => { setTileNavState('unread'); handleTabChange('messages'); },
                          },
                          {
                            count: eventsWithBookingsCount,
                            label: 'Bookings',
                            bg: 'from-blue-50/70 to-white border-blue-100/60',
                            onClick: () => { setTileNavState('bookings'); handleTabChange('events'); },
                          },
                          {
                            count: paymentsDueCount,
                            label: 'Payments',
                            bg: 'from-amber-50/70 to-white border-amber-100/60',
                            onClick: () => { setTileNavState('payments-due'); handleTabChange('customer-payments'); },
                          },
                        ].map(({ count, label, bg, onClick }) => (
                          <button
                            key={label}
                            onClick={onClick}
                            className={`flex flex-col items-center gap-0.5 py-3.5 rounded-2xl bg-gradient-to-br ${bg} border transition-all active:scale-[0.97]`}
                            data-testid={`stat-${label.toLowerCase()}`}
                          >
                            <span className="text-xl font-bold text-[#2A2A26]">{count}</span>
                            <span className="text-[11px] text-[#8A8A82]">{label}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* ═══ 3. NEXT STEPS (Action Carousel) ═══ */}
                    {actionItems.length > 0 ? (
                      <div data-testid="next-steps-section">
                        <h3 className="text-[11px] font-medium text-[#9A9A94] uppercase tracking-wider px-1 mb-3">Next Steps</h3>
                        <div className="relative group/ns-carousel">
                          {/* Left arrow */}
                          {nextStepsScrollState.hasOverflow && !nextStepsScrollState.atStart && (
                            <button
                              data-testid="next-steps-carousel-prev"
                              onClick={() => scrollCarousel(nextStepsCarouselRef, 'left')}
                              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 hidden sm:flex items-center justify-center w-8 h-8 rounded-full bg-white/90 border border-slate-200 shadow-md hover:bg-white hover:shadow-lg transition-all -ml-1"
                              aria-label="Previous actions"
                            >
                              <ChevronLeft className="w-4 h-4 text-slate-600" />
                            </button>
                          )}
                          {/* Right arrow */}
                          {nextStepsScrollState.hasOverflow && !nextStepsScrollState.atEnd && (
                            <button
                              data-testid="next-steps-carousel-next"
                              onClick={() => scrollCarousel(nextStepsCarouselRef, 'right')}
                              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 hidden sm:flex items-center justify-center w-8 h-8 rounded-full bg-white/90 border border-slate-200 shadow-md hover:bg-white hover:shadow-lg transition-all -mr-1"
                              aria-label="Next actions"
                            >
                              <ChevronRight className="w-4 h-4 text-slate-600" />
                            </button>
                          )}
                          {/* Edge fades */}
                          {nextStepsScrollState.hasOverflow && !nextStepsScrollState.atStart && (
                            <div className="absolute left-0 top-0 bottom-2 w-6 bg-gradient-to-r from-white/80 to-transparent z-[5] pointer-events-none rounded-l-[16px]" />
                          )}
                          {nextStepsScrollState.hasOverflow && !nextStepsScrollState.atEnd && (
                            <div className="absolute right-0 top-0 bottom-2 w-6 bg-gradient-to-l from-white/80 to-transparent z-[5] pointer-events-none rounded-r-[16px]" />
                          )}
                          <div
                            ref={nextStepsCarouselRef}
                            className="flex gap-3.5 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-1 px-1"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                            data-testid="action-carousel"
                          >
                          {displayItems.map((item) => (
                            <div
                              key={item.id}
                              onClick={() => item.eventId && navigate(`/events/${item.eventId}`)}
                              className="snap-start shrink-0 w-[82%] sm:w-[60%] md:w-[45%] max-w-[320px] rounded-[16px] border border-[#E6E6E0] bg-white flex flex-col p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] cursor-pointer transition-all duration-200 hover:border-[#C6A96B]/40 hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] active:scale-[0.98]"
                              style={{ minHeight: '180px' }}
                              data-testid={`action-card-${item.id}`}
                            >
                              <div className="flex items-start gap-3.5">
                                <div className="h-10 w-10 rounded-xl bg-[#F1F1EC] flex items-center justify-center shrink-0">
                                  {item.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[13px] font-semibold text-[#1C1C1C] leading-snug line-clamp-2 min-h-[36px]">{item.title}</p>
                                  <p className="text-xs text-[#6F6F6F] mt-1 truncate">{item.detail}</p>
                                </div>
                              </div>
                              <Button
                                onClick={(e) => { e.stopPropagation(); item.action?.(); }}
                                size="sm"
                                disabled={!item.action}
                                className="bg-[#C6A96B] hover:bg-[#B89655] rounded-full text-xs px-5 h-9 w-full mt-auto shadow-sm"
                                data-testid={`action-cta-${item.id}`}
                              >
                                {item.cta}
                              </Button>
                            </div>
                          ))}
                        </div>
                        </div>
                      </div>
                    ) : serverNextSteps.length > 0 ? (
                      <div data-testid="next-steps-caught-up">
                        <h3 className="text-[11px] font-medium text-[#9A9A94] uppercase tracking-wider px-1 mb-3">Next Steps</h3>
                        <div className="rounded-2xl border border-[#E6E6E0] bg-white/60 p-5 text-center">
                          <Check className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
                          <p className="text-sm font-medium text-[#1C1C1C]">You're all caught up</p>
                          <p className="text-xs text-[#8A8A82] mt-1">Waiting on vendors to respond — nothing to do right now</p>
                        </div>
                      </div>
                    ) : null}

                    {/* ═══ Next Steps removed — vendor rows in Event Detail are the single action surface ═══ */}

                    {/* Floating banners removed — announcements live in Updates section */}
                    
                  </div>
                );
              })()}
            </div>
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            <div>
              {loading ? (
                <InquiryListSkeleton count={3} />
              ) : inquiries.length === 0 ? (
                <div className="text-center py-16">
                  <Search className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                  <h3 className="text-[15px] font-semibold text-gray-900 mb-1">No Requests Yet</h3>
                  <p className="text-[13px] text-gray-500 mb-5">Browse vendors and send your first request</p>
                  <Button onClick={() => navigate('/browse')} className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-9 px-5 text-[13px]">Browse Service Providers</Button>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Active Requests */}
                  {inquiries.filter(inq => !['declined', 'cancelled', 'completed'].includes(inq.status)).length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 px-1 mb-2.5">
                        <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Active</span>
                      </div>
                      <div className="space-y-3">
                        {inquiries
                          .filter(inq => !['declined', 'cancelled', 'completed'].includes(inq.status))
                          .map((inquiry) => (
                            <InquiryCard
                              key={inquiry.id}
                              inquiry={inquiry}
                              bookings={bookings}
                              disputes={disputes}
                              loading={loading}
                              addingCustomerNote={addingCustomerNote}
                              newCustomerNote={newCustomerNote}
                              formatDate={formatShortDate}
                              handleOpenMessaging={handleOpenMessaging}
                              setDisputingInquiry={setDisputingInquiry}
                              navigate={navigate}
                              inquiries={inquiries}
                              setAddingCustomerNote={setAddingCustomerNote}
                              setNewCustomerNote={setNewCustomerNote}
                              submitCustomerNote={submitCustomerNote}
                              setSelectedBooking={setSelectedBooking}
                              setShowPaymentCheckout={setShowPaymentCheckout}
                              setShowServiceAgreement={setShowServiceAgreement}
                              onBookingCancelled={handleBookingCancelled}
                              onReviewClick={openReviewDialog}
                              reviews={existingReviews}
                            />
                          ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Closed Requests */}
                  {inquiries.filter(inq => ['declined', 'cancelled', 'completed'].includes(inq.status)).length > 0 && (
                    <div className="pt-2">
                      <div className="flex items-center gap-2 px-1 mb-2.5">
                        <div className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Closed</span>
                      </div>
                      <div className="space-y-3 opacity-70">
                        {inquiries
                          .filter(inq => ['declined', 'cancelled', 'completed'].includes(inq.status))
                          .map((inquiry) => (
                            <InquiryCard
                              key={inquiry.id}
                              inquiry={inquiry}
                              bookings={bookings}
                              disputes={disputes}
                              loading={loading}
                              addingCustomerNote={addingCustomerNote}
                              newCustomerNote={newCustomerNote}
                              formatDate={formatShortDate}
                              handleOpenMessaging={handleOpenMessaging}
                              setDisputingInquiry={setDisputingInquiry}
                              navigate={navigate}
                              inquiries={inquiries}
                              setAddingCustomerNote={setAddingCustomerNote}
                              setNewCustomerNote={setNewCustomerNote}
                              submitCustomerNote={submitCustomerNote}
                              setSelectedBooking={setSelectedBooking}
                              setShowPaymentCheckout={setShowPaymentCheckout}
                              setShowServiceAgreement={setShowServiceAgreement}
                              onBookingCancelled={handleBookingCancelled}
                              onReviewClick={openReviewDialog}
                              reviews={existingReviews}
                            />
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Chats Tab */}
          <TabsContent value="messages">
            <div className="space-y-6 pb-20 md:pb-0">
              <div className="hidden md:block">
                <h2 className="text-lg font-semibold text-gray-900">Chats</h2>
                <p className="text-sm text-gray-400 mt-0.5">Conversations with your service providers</p>
              </div>
              {loading ? (
                <MessageListSkeleton count={4} />
              ) : inquiries.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-100/80">
                  <MessageSquare className="h-12 w-12 text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium mb-2">No conversations yet</p>
                  <p className="text-sm text-gray-400 mb-6 max-w-xs mx-auto">Browse vendors and send a request to start chatting</p>
                  <Button
                    onClick={() => navigate('/browse')}
                    className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-8"
                  >
                    Browse Vendors
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Unread section — shown first when arriving from Unread tile */}
                  {tileNavState === 'unread' && inquiries.filter(i => i.unread_count > 0).length > 0 && (
                    <div className="space-y-1" data-testid="unread-messages-section">
                      <h3 className="text-xs font-semibold text-purple-500 uppercase tracking-wider px-1 mb-2 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                        Unread ({inquiries.filter(i => i.unread_count > 0).length})
                      </h3>
                      <div className="space-y-1 bg-white rounded-2xl border border-purple-100/60 divide-y divide-gray-50 overflow-hidden">
                        {inquiries
                          .filter(i => i.unread_count > 0)
                          .map((inquiry) => (
                            <MessageConversationCard
                              key={inquiry.id}
                              inquiry={inquiry}
                              booking={(bookings || []).find(b => b.inquiry_id === inquiry.id)}
                              onClick={handleOpenMessaging}
                            />
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Active Chats */}
                  {inquiries.filter(i => !['cancelled', 'completed', 'declined'].includes(i.status) && (tileNavState !== 'unread' || !i.unread_count)).length > 0 && (
                    <div className="space-y-1">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-2 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Active
                      </h3>
                      <div className="space-y-1 bg-white rounded-2xl border border-gray-100/60 divide-y divide-gray-50 overflow-hidden">
                          {inquiries
                            .filter(i => !['cancelled', 'completed', 'declined'].includes(i.status) && (tileNavState !== 'unread' || !i.unread_count))
                            .map((inquiry) => (
                              <MessageConversationCard
                                key={inquiry.id}
                                inquiry={inquiry}
                                booking={(bookings || []).find(b => b.inquiry_id === inquiry.id)}
                                onClick={handleOpenMessaging}
                              />
                            ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Past Chats */}
                    {inquiries.filter(i => ['cancelled', 'completed', 'declined'].includes(i.status)).length > 0 && (
                      <div className="space-y-1">
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-2 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                          Past
                        </h3>
                        <div className="space-y-1 bg-white/60 rounded-2xl border border-gray-100/60 divide-y divide-gray-50 overflow-hidden opacity-70">
                          {inquiries
                            .filter(i => ['cancelled', 'completed', 'declined'].includes(i.status))
                            .map((inquiry) => (
                              <MessageConversationCard
                                key={inquiry.id}
                                inquiry={inquiry}
                                onClick={handleOpenMessaging}
                              />
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
          </TabsContent>

          {/* My Events Tab */}
          <TabsContent value="events">
            <div className="space-y-6 relative pb-20 md:pb-0">
              {/* Header — desktop only */}
              <div className="hidden md:flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">My Events</h2>
                <button
                  onClick={() => navigate('/plan-event')}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium shadow-sm hover:shadow-md transition-all active:scale-95"
                  data-testid="create-event-btn-desktop"
                  aria-label="Plan new event"
                >
                  <Plus className="w-4 h-4" strokeWidth={2.5} />
                  Plan New Event
                </button>
              </div>

              {loading ? (
                <div className="space-y-3">
                  {[1,2,3].map(i => (
                    <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : events.length === 0 ? (
                /* Phase-5 premium empty state — ambient CeleyDelightLayer
                   ("idle" mode), emotional copy, and a single clear CTA.
                   Renders only when the user truly has zero events; existing
                   EventsList path is unchanged. Mobile-first: the card
                   fills the column but stays under 70vh so the FAB and tab
                   bar remain reachable. */
                <div
                  className="relative overflow-hidden text-center py-14 sm:py-20 bg-white rounded-[20px] border border-[#E6E6E0]"
                  data-testid="customer-empty-state"
                >
                  {/* Ambient delight — subtle, non-interactive */}
                  <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                    <CeleyDelightLayer mode="idle" />
                  </div>

                  {/* Foreground content */}
                  <div className="relative px-6">
                    <div
                      className="mx-auto mb-5 h-16 w-16 rounded-full flex items-center justify-center"
                      style={{
                        background: 'radial-gradient(circle at 30% 30%, rgba(198,169,107,0.20), rgba(198,169,107,0.05) 70%)',
                      }}
                    >
                      <PartyPopper
                        className="h-8 w-8"
                        style={{ color: '#C6A96B' }}
                      />
                    </div>
                    <h3
                      className="text-lg sm:text-xl font-semibold text-gray-900"
                      data-testid="customer-empty-state-title"
                    >
                      Let&apos;s plan something worth celebrating
                    </h3>
                    <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
                      Every big moment starts with a small idea. Tell us about yours and we&apos;ll gather the right people to make it real.
                    </p>
                    <Button
                      onClick={() => navigate('/plan-event')}
                      className="mt-7 bg-emerald-600 hover:bg-emerald-700 rounded-full px-8 h-11 text-sm font-semibold shadow-sm"
                      data-testid="customer-empty-state-cta"
                    >
                      Plan your first event
                    </Button>
                  </div>
                </div>
              ) : (
                <EventsList 
                  key={`events-${tileNavState || 'default'}`}
                  events={events} 
                  inquiries={inquiries} 
                  navigate={navigate}
                  API={API}
                  tileNavState={tileNavState}
                  bookings={bookings}
                  briefReadyEventIds={briefReadyEventIds}
                />
              )}

              {/* Mobile FAB */}
              <button
                onClick={() => navigate('/plan-event')}
                className="md:hidden fixed z-40 w-14 h-14 flex items-center justify-center rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/30 active:scale-90 transition-all"
                style={{ bottom: 'calc(5.5rem + env(safe-area-inset-bottom, 0px))', right: '1.25rem' }}
                data-testid="create-event-btn"
                aria-label="Plan new event"
              >
                <Plus className="w-6 h-6" strokeWidth={2.5} />
              </button>
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div>
              {completedInquiries.length === 0 ? (
                <div className="text-center py-16">
                  <Star className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                  <h3 className="text-[15px] font-semibold text-gray-900 mb-1">No Reviews Yet</h3>
                  <p className="text-[13px] text-gray-500">Once a service is completed, you can leave a review here.</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Services awaiting review */}
                  {reviewableInquiries.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 px-1 mb-2.5">
                        <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Awaiting Review ({reviewableInquiries.length})</span>
                      </div>
                      <div className="space-y-3">
                        {reviewableInquiries.map((inquiry) => (
                          <div key={inquiry.id} className="bg-white rounded-xl border border-amber-100 p-4">
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex-1 min-w-0">
                                <Link 
                                  to={`/vendors/${inquiry.vendor_id}`}
                                  className="text-[14px] font-bold text-gray-900 hover:text-emerald-600 transition-colors"
                                >
                                  {inquiry.vendor_name || 'Service Provider'}
                                </Link>
                                <p className="text-[12px] text-gray-500 mt-0.5">
                                  {inquiry.event_type} · {formatShortDate(inquiry.event_date)}
                                </p>
                              </div>
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0">Awaiting Review</span>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => openReviewDialog(inquiry)}
                              className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-8 px-4 text-[12px]"
                            >
                              <Star className="h-3.5 w-3.5 mr-1.5" />
                              Leave Review
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Already reviewed */}
                  {reviewedInquiries.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 px-1 mb-2.5">
                        <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Reviewed ({reviewedInquiries.length})</span>
                      </div>
                      <div className="space-y-3">
                        {reviewedInquiries.map((inquiry) => {
                          const review = existingReviews.find(r => r.inquiry_id === inquiry.id);
                          return (
                            <div key={inquiry.id} className="bg-white rounded-xl border border-gray-100 p-4">
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="flex-1 min-w-0">
                                  <Link 
                                    to={`/vendors/${inquiry.vendor_id}`}
                                    className="text-[14px] font-bold text-gray-900 hover:text-emerald-600 transition-colors"
                                  >
                                    {inquiry.vendor_name || 'Service Provider'}
                                  </Link>
                                  <p className="text-[12px] text-gray-500 mt-0.5">
                                    {inquiry.event_type} · {formatShortDate(inquiry.event_date)}
                                  </p>
                                </div>
                                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 shrink-0">Reviewed</span>
                              </div>
                              {review && (
                                <div className="mt-2 pt-2 border-t border-gray-50">
                                  <div className="flex items-center gap-0.5 mb-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Star
                                        key={star}
                                        className={`h-3.5 w-3.5 ${star <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`}
                                      />
                                    ))}
                                  </div>
                                  <p className="text-[13px] text-gray-600 italic leading-relaxed">&ldquo;{review.comment}&rdquo;</p>
                                  {!review.is_approved && (
                                    <p className="text-[11px] text-amber-600 mt-1.5">Pending admin approval</p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* All reviewed banner */}
                  {reviewableInquiries.length === 0 && reviewedInquiries.length > 0 && (
                    <div className="text-center py-6 bg-emerald-50/60 rounded-xl border border-emerald-100">
                      <Star className="h-6 w-6 text-emerald-500 mx-auto mb-1.5" />
                      <p className="text-[13px] text-emerald-700 font-semibold">All services reviewed!</p>
                      <p className="text-[12px] text-emerald-600 mt-0.5">Thank you for your feedback.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Profile/Account Tab - Now using sectioned navigation */}
          <TabsContent value="profile">
            <AccountPage
              variant="customer"
              onNavigate={(tab) => handleTabChange(tab)}
              onLogout={() => {
                // Feb 2026: logout() owns the route transition. A caller-
                // side navigate raced ahead of setUser(null) and caused
                // AuthPage to redirect a still-truthy user back to the
                // dashboard.
                logout();
              }}
              onFeedbackClick={() => setFeedbackOpen(true)}
              eventsCount={events.length}
            />
          </TabsContent>

          {/* Profile Edit Tab - Full form view */}
          <TabsContent value="profile-edit">
            <div className="mb-3">
              <Button variant="ghost" size="sm" onClick={() => handleTabChange('profile')} className="text-gray-500 text-[13px] rounded-full">
                &larr; Back to Account
              </Button>
            </div>
            <div>
              <div className="mb-4">
                <h3 className="text-[15px] font-bold text-gray-900">Edit Profile</h3>
                <p className="text-[12px] text-gray-400 mt-0.5">Update your personal information</p>
              </div>
              <ProfileForm
                  profileData={profileData}
                  setProfileData={setProfileData}
                  uploadingImage={uploadingImage}
                  profileSaved={profileSaved}
                  loading={loading}
                  handleProfileImageUpload={handleProfileImageUpload}
                  handleProfileSubmit={handleProfileSubmit}
                  getImageUrl={getImageUrl}
                />
            </div>
          </TabsContent>

          {/* Profile Security Tab - Full form view */}
          <TabsContent value="profile-security">
            <div className="mb-3">
              <Button variant="ghost" size="sm" onClick={() => handleTabChange('profile')} className="text-gray-500 text-[13px] rounded-full">
                &larr; Back to Account
              </Button>
            </div>
            <div>
              <div className="mb-4">
                <h3 className="text-[15px] font-bold text-gray-900">Account & Security</h3>
                <p className="text-[12px] text-gray-400 mt-0.5">Manage your security settings</p>
              </div>
              <AccountSettings
                  user={user}
                  newEmail={newEmail}
                  setNewEmail={setNewEmail}
                  updateEmail={updateEmail}
                  updatingEmail={updatingEmail}
                  newPhone={newPhone}
                  setNewPhone={setNewPhone}
                  handleUpdatePhone={handleUpdatePhone}
                  updatingPhone={updatingPhone}
                  passwordData={passwordData}
                  setPasswordData={setPasswordData}
                  handlePasswordChange={handlePasswordChange}
                  updatingPassword={updatingPassword}
                  securityQuestions={securityQuestions}
                  securityAnswers={securityAnswers}
                  setSecurityAnswers={setSecurityAnswers}
                  currentPasswordForSecurity={currentPasswordForSecurity}
                  setCurrentPasswordForSecurity={setCurrentPasswordForSecurity}
                  handleSecurityQuestionsUpdate={handleSecurityQuestionsUpdate}
                  updatingSecurityQuestions={updatingSecurityQuestions}
                />
            </div>
          </TabsContent>

          {/* Help & Support Tab */}
          <TabsContent value="help">
            <div className="mb-3">
              <Button variant="ghost" size="sm" onClick={() => handleTabChange('profile')} className="text-gray-500 text-[13px] rounded-full">
                &larr; Back to Account
              </Button>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-4">
                <HelpCircle className="h-4 w-4 text-blue-500" />
                <h3 className="text-[15px] font-bold text-gray-900">Help & Support</h3>
              </div>
              <HelpSupportSection 
                userRole="customer"
                onFeedbackClick={() => setFeedbackOpen(true)}
                embedded={true}
              />
            </div>
          </TabsContent>

          {/* Customer Payments Tab — two strictly separated views */}
          <TabsContent value="customer-payments">
            {tileNavState === 'payments-due' ? (
              /* ═══ PAYMENTS DUE (from Home tile) — action-oriented ═══ */
              <div>
                <div className="mb-3">
                  <Button variant="ghost" size="sm" onClick={() => handleBottomNav('welcome')} className="text-gray-500 text-[13px] rounded-full" data-testid="payments-due-back-btn">
                    &larr; Back
                  </Button>
                </div>
                <div data-testid="payments-due-section">
                  <div className="flex items-center gap-2 mb-4">
                    <CreditCard className="h-4 w-4 text-amber-500" />
                    <h3 className="text-[15px] font-bold text-gray-900">Payments Due</h3>
                    <span className="ml-auto text-xs text-gray-400">{paymentsDue.length} pending</span>
                  </div>

                  {paymentsDue.length === 0 ? (
                    <div className="text-center py-10 bg-emerald-50/60 rounded-xl border border-emerald-100">
                      <Check className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
                      <p className="text-[13px] text-emerald-700 font-semibold">All caught up!</p>
                      <p className="text-[12px] text-emerald-600 mt-0.5">No payments due right now.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {paymentsDue.map((pd) => {
                        const isUrgent = pd.urgency === 'expired' || pd.urgency === 'overdue' || pd.urgency === 'warning';
                        const bk = bookings.find(b => b.id === pd.booking_id);
                        return (
                          <div
                            key={pd.id}
                            className={`flex items-center gap-3.5 p-4 rounded-xl border bg-white cursor-pointer transition-all hover:bg-gray-50/50 ${
                              isUrgent ? 'border-red-200 ring-1 ring-red-100' : 'border-gray-100/80'
                            }`}
                            onClick={() => {
                              if (bk) {
                                setSelectedBooking(bk);
                                setShowPaymentCheckout(true);
                              }
                            }}
                            data-testid={`payment-due-${pd.id}`}
                          >
                            <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${isUrgent ? 'bg-red-50' : 'bg-amber-50'}`}>
                              <CreditCard className={`h-4.5 w-4.5 ${isUrgent ? 'text-red-600' : 'text-amber-600'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 leading-snug">{pd.vendor_name}</p>
                              <p className="text-xs text-gray-400 mt-0.5 truncate">
                                {pd.payment_type === 'deposit' ? 'Deposit' : 'Final balance'} · {pd.event_type}
                                {pd.countdown && <span className={`ml-1 ${isUrgent ? 'text-red-500 font-medium' : ''}`}> · {pd.countdown}</span>}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-sm font-bold text-gray-900">${pd.amount?.toLocaleString()}</p>
                              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                isUrgent ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
                              }`}>
                                {pd.urgency === 'expired' ? 'Expired' : pd.urgency === 'overdue' ? 'Overdue' : pd.urgency === 'warning' ? 'Due Soon' : 'Due'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* ═══ PAYMENT HISTORY (from Account) — passive records ═══ */
              <div>
                <div className="mb-3">
                  <Button variant="ghost" size="sm" onClick={() => handleTabChange('profile')} className="text-gray-500 text-[13px] rounded-full" data-testid="payment-history-back-btn">
                    &larr; Back to Account
                  </Button>
                </div>
                <div data-testid="payment-history-section">
                  <div className="flex items-center gap-2 mb-4">
                    <CreditCard className="h-4 w-4 text-emerald-500" />
                    <h3 className="text-[15px] font-bold text-gray-900">Payments — History & Receipts</h3>
                  </div>
                  {loadingPayments ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
                      ))}
                    </div>
                  ) : customerPayments.length === 0 ? (
                    <div className="text-center py-16">
                      <CreditCard className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                      <h3 className="text-[15px] font-semibold text-gray-900 mb-1">No Payments Yet</h3>
                      <p className="text-[13px] text-gray-500">Your payment history will appear here once you complete a transaction.</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden divide-y divide-gray-50">
                      {customerPayments.map((payment) => (
                        <button
                          key={payment.id}
                          onClick={() => {
                            fetchPaymentDetails(payment.id);
                            handleTabChange('payment-receipt');
                          }}
                          className="w-full px-4 py-3.5 hover:bg-gray-50/60 transition-colors text-left flex items-center gap-3"
                          data-testid={`payment-history-${payment.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-[14px] font-semibold text-gray-900 truncate">{payment.vendor_name}</p>
                            <p className="text-[12px] text-gray-500 mt-0.5">
                              {payment.service}
                              <span className="text-gray-300 mx-1.5">&middot;</span>
                              <span className="text-gray-400">
                                {payment.paid_at 
                                  ? new Date(payment.paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                  : ''
                                }
                              </span>
                            </p>
                          </div>
                          <div className="text-right shrink-0 flex flex-col items-end gap-1">
                            <p className="text-[14px] font-bold text-gray-900">${payment.amount?.toFixed(2)}</p>
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                              payment.status === 'succeeded' ? 'bg-emerald-50 text-emerald-700' :
                              payment.status === 'refunded' ? 'bg-purple-50 text-purple-700' :
                              'bg-gray-50 text-gray-600'
                            }`}>
                              {payment.status === 'succeeded' ? 'Paid' :
                               payment.status === 'refunded' ? 'Refunded' : 'Completed'}
                            </span>
                          </div>
                          <ChevronRight className="h-4 w-4 text-gray-300 shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Payment Receipt Tab */}
          <TabsContent value="payment-receipt">
            <div className="mb-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setSelectedPayment(null);
                  handleTabChange('customer-payments');
                }}
                className="text-gray-600"
              >
                ← Back to Payments
              </Button>
            </div>
            {selectedPayment ? (
              <Card>
                <CardHeader className="text-center border-b pb-6">
                  {/* Celey Branding */}
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <img src="/celeyapp-icon-green.png" alt="Celey" className="h-8 w-8" />
                    <span className="text-lg font-semibold text-gray-700">Celey</span>
                  </div>
                  <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    {selectedPayment.status === 'succeeded' ? (
                      <CheckCircle className="h-8 w-8 text-emerald-600" />
                    ) : selectedPayment.is_upcoming ? (
                      <Calendar className="h-8 w-8 text-amber-600" />
                    ) : (
                      <CreditCard className="h-8 w-8 text-gray-600" />
                    )}
                  </div>
                  <CardTitle className="text-xl">
                    {selectedPayment.is_upcoming ? 'Upcoming Payment' : 'Payment Receipt'}
                  </CardTitle>
                  <CardDescription>
                    {selectedPayment.payment_type_label}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="text-center mb-6">
                    <p className="text-3xl font-bold text-gray-900">${selectedPayment.amount?.toFixed(2)}</p>
                    <Badge 
                      className={`mt-2 ${
                        selectedPayment.status === 'succeeded' ? 'bg-emerald-100 text-emerald-800' :
                        selectedPayment.is_upcoming ? 'bg-amber-100 text-amber-800' :
                        selectedPayment.status === 'refunded' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {selectedPayment.status_label || (selectedPayment.is_upcoming ? 'Due' : 'Pending')}
                    </Badge>
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Provider</span>
                      <span className="font-medium">{selectedPayment.vendor_name}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Service</span>
                      <span className="font-medium">{selectedPayment.service}</span>
                    </div>
                    {selectedPayment.event_date && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-500">Event Date</span>
                        <span className="font-medium">
                          {formatLongDate(selectedPayment.event_date)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-500">Payment Type</span>
                      <span className="font-medium">{selectedPayment.payment_type_label}</span>
                    </div>
                    {selectedPayment.payment_method && !selectedPayment.is_upcoming && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-500">Payment Method</span>
                        <span className="font-medium capitalize">{selectedPayment.payment_method}</span>
                      </div>
                    )}
                    {selectedPayment.transaction_id && !selectedPayment.is_upcoming && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-500">Transaction ID</span>
                        <span className="font-medium text-xs font-mono">{selectedPayment.transaction_id.substring(0, 20)}...</span>
                      </div>
                    )}
                    {selectedPayment.paid_at && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-500">Date & Time</span>
                        <span className="font-medium">
                          {new Date(selectedPayment.paid_at).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {selectedPayment.is_upcoming && selectedPayment.due_date && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-500">Due Date</span>
                        <span className="font-medium text-amber-600">
                          {new Date(selectedPayment.due_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Copy Receipt Details - only for completed payments */}
                  {!selectedPayment.is_upcoming && selectedPayment.status === 'succeeded' && (
                    <div className="pt-4">
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={() => copyReceiptDetails(selectedPayment)}
                        data-testid="copy-receipt-btn"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy receipt details
                      </Button>
                    </div>
                  )}

                  {selectedPayment.is_upcoming && selectedPayment.can_pay_now && (
                    <div className="pt-4">
                      <Button 
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => {
                          // Navigate to booking to pay
                          handleTabChange('inquiries');
                        }}
                      >
                        Pay Now
                      </Button>
                      <p className="text-xs text-gray-500 text-center mt-2">
                        You&apos;ll be redirected to complete payment securely via Stripe.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading payment details...</p>
              </div>
            )}
          </TabsContent>

          {/* Payment Success Tab */}
          <TabsContent value="payment-success">
            <Card>
              <CardContent className="pt-8 pb-8 text-center">
                {/* Celey Branding */}
                <div className="flex items-center justify-center gap-2 mb-6">
                  <img src="/celeyapp-icon-green.png" alt="Celey" className="h-8 w-8" />
                  <span className="text-lg font-semibold text-gray-700">Celey</span>
                </div>
                <div className="mx-auto w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="h-10 w-10 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
                <p className="text-gray-500 mb-6">
                  Your payment has been processed successfully.
                </p>
                
                {paymentSuccessData && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6 max-w-sm mx-auto">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Amount Paid</span>
                      <span className="font-semibold">${paymentSuccessData.amount?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Payment Type</span>
                      <span className="font-medium capitalize">
                        {paymentSuccessData.payment_type === 'deposit' ? 'Deposit' : 'Final Payment'}
                      </span>
                    </div>
                  </div>
                )}

                <div className="space-y-3 max-w-xs mx-auto">
                  {paymentSuccessData?.payment_id && (
                    <Button 
                      className="w-full"
                      onClick={() => {
                        fetchPaymentDetails(paymentSuccessData.payment_id);
                        handleTabChange('payment-receipt');
                      }}
                    >
                      View Receipt
                    </Button>
                  )}
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      // Modern Celey flow: post-payment → Event command center.
                      // Look up event_id from the loaded bookings list using the
                      // booking_id returned by /customer/payments/by-session.
                      // Falls back to My Events list (NOT the legacy My Bookings
                      // view at ?tab=inquiries) if event_id isn't available yet.
                      const bookingId = paymentSuccessData?.booking_id;
                      const eventId = bookings.find(b => b.id === bookingId)?.event_id;
                      if (eventId) {
                        navigate(`/events/${eventId}`);
                      } else {
                        handleTabChange('events');
                      }
                    }}
                    data-testid="payment-success-back-to-event-btn"
                  >
                    Back to My Event
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Mobile Bottom Tab Bar */}
      <BottomTabBar 
        activeTab={activeTab}
        onTabChange={handleTabChange}
        variant="customer"
      />

      {/* Customer Dialogs */}
      <CustomerDialogs
        showReviewDialog={showReviewDialog}
        setShowReviewDialog={setShowReviewDialog}
        selectedInquiry={selectedInquiry}
        setSelectedInquiry={setSelectedInquiry}
        reviewData={reviewData}
        setReviewData={setReviewData}
        submitReview={submitReview}
        loading={loading}
        formatShortDate={formatShortDate}
        uploadingReviewImage={uploadingReviewImage}
        handleReviewImageUpload={handleReviewImageUpload}
        removeReviewImage={removeReviewImage}
        getImageUrl={getImageUrl}
        disputingInquiry={disputingInquiry}
        setDisputingInquiry={setDisputingInquiry}
        disputeData={disputeData}
        setDisputeData={setDisputeData}
        submitDispute={submitDispute}
        navigate={navigate}
      />

      {/* Feedback Dialog */}
      <FeedbackDialog 
        isOpen={feedbackOpen} 
        onClose={() => {
          setFeedbackOpen(false);
          setReportingInquiry(null);
        }} 
        userRole="customer"
        inquiries={inquiries}
        preSelectedInquiry={reportingInquiry}
      />

      {/* Service Agreement Dialog */}
      <ServiceAgreementDialog
        bookingId={selectedBooking?.id}
        isOpen={showServiceAgreement}
        onClose={() => setShowServiceAgreement(false)}
        onAccepted={handleServiceAgreementAccepted}
        userRole="customer"
      />

      {/* Payment Checkout Dialog */}
      <PaymentCheckout
        bookingId={selectedBooking?.id}
        isOpen={showPaymentCheckout}
        onClose={() => setShowPaymentCheckout(false)}
        onSuccess={() => {
          setShowPaymentCheckout(false);
          fetchData();
        }}
        context={(() => {
          if (!selectedBooking) return null;
          const inq = inquiries.find(i => i.id === selectedBooking.inquiry_id);
          const pd = paymentsDue.find(p => p.booking_id === selectedBooking.id);
          const eventId = inq?.event_id || pd?.event_id || selectedBooking?.event_id || null;
          const evt = eventId ? events.find(e => e.id === eventId) : null;
          return {
            eventId: evt?.id || eventId,
            eventName: evt?.event_type || inq?.event_type || pd?.event_type || null,
            vendorName: inq?.vendor_name || pd?.vendor_name || null,
            vendorService: inq ? vendorServiceMap[inq.vendor_id] : null,
            eventDate: inq?.event_date || pd?.event_date || null,
            location: inq?.location || null,
          };
        })()}
      />

      {/* Messaging Thread — Feature-flagged via ConversationSwitcher */}
      <ConversationSwitcher
        inquiryId={selectedInquiryForMessaging?.id}
        isOpen={showMessaging}
        onClose={handleCloseMessaging}
        userRole="customer"
        onProposalAccepted={handleProposalAccepted}
        inquiryStatus={selectedInquiryForMessaging?.status}
        __diagSelectedInquiry={selectedInquiryForMessaging}
        headerVendor={selectedInquiryForMessaging ? {
          business_name: selectedInquiryForMessaging.vendor_name,
          vendor_name: selectedInquiryForMessaging.vendor_name,
          profile_photo: selectedInquiryForMessaging.profile_photo,
          owner_photo: selectedInquiryForMessaging.owner_photo,
          vendor_image: selectedInquiryForMessaging.vendor_image,
        } : null}
        scrollToPayment={scrollToPayment}
        onPayNow={(bookingId) => {
          const bk = bookings.find(b => b.id === bookingId);
          if (bk) setSelectedBooking(bk);
          else setSelectedBooking({ id: bookingId });
          setShowPaymentCheckout(true);
        }}
        onLeaveReview={(inquiryId) => {
          const inq = inquiries.find(i => i.id === inquiryId);
          if (inq) openReviewDialog(inq);
        }}
      />

      {/* Image Crop Dialog */}
      <ImageCropDialog
        isOpen={showCropDialog}
        onClose={() => {
          setShowCropDialog(false);
          setImageToCrop(null);
        }}
        imageSrc={imageToCrop}
        onCropComplete={handleCroppedImage}
      />
    </div>
  );
};

export default CustomerDashboard;
